import { html, css, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { icons } from "../icons.ts";
import { formatMs, formatDurationHuman } from "../format.ts";
import type { ProjectExecute, ProgressLogEntry, EadFmNodeRun } from "../../types/ead-types.ts";
import type { ChatPanelConfig } from "../../types/ead-types.ts";
import { executionsStore } from "../executions-store.ts";
import {
  clearPersistedRunActivities,
  mergeRunActivitiesPersisted,
} from "../run-activity-cache.ts";
import {
  formatConversationalActivityProse,
  formatOperatorActivityLine,
  stripOperatorActivityPhasePrefix,
  type OperatorActivityPhase,
} from "../activity-format.ts";
import {
  answerLanguageLabel,
  defaultAnswerLanguage,
  normalizeAnswerLanguage,
  type AiAnswerLanguage,
} from "../ai-answer-language.ts";
import {
  isLiveProjectExecute,
  ProjectRunStatus,
  projectRunStatusLabel,
  projectRunStatusLabelForExecution,
  readProjectRunStatus,
  storedProjectRunStatus,
} from "../run-status.ts";
import {
  isExecutionValidForInteractiveLearning,
  shouldShowContinuousLearningToggle,
} from "../pfm-run-eligibility.ts";
import { hermesClient } from "../hermes-singleton.ts";
import { runGatewayPoll } from "../gateway-poll-queue.ts";
import { canPollGateway } from "../poll-coordinator.ts";
import { resolveExecutionReportUrl } from "../execution-report-url.ts";
import { budgetCountdownStartMs } from "../run-budget-clock.ts";
import { buildRunBriefingMarkdown } from "../run-briefing.ts";
import {
  isAwaitingLoginCredentials,
  isLoginCheckpoint,
  isLoginSuccessful,
  loginGateOperatorHint,
  resolveInitializationReviewStatus,
  resolveLoginPhaseStatus,
  formatLoginPhaseDisplay,
} from "../login-phase.ts";
import {
  isRunChatSessionLive,
  resolveRunSessionKey,
  runSessionKeyFromGateway,
} from "../run-session-key.ts";
import {
  buildPfmDeliveryValidationBanner,
  shouldPollPfmDeliveryValidation,
  type PfmDeliveryValidationPayload,
} from "../pfm-delivery-validation.ts";
import {
  loadPfmNodeReport,
  loadPfmMindmap,
  loadVisitedPfmNodes,
  loadActivePfmNode,
  saveVisitedPfmNode,
} from "../../local-storage.ts";
import {
  isExecutionEligibleForPfmInheritance,
  sortExecutionsByStartTimeDesc,
  sortExecutionsByRunNumberDesc,
} from "../pfm-run-eligibility.ts";
import {
  buildPfmMindmapHeaderLabel,
  findLatestTemplatePfmTreeRun,
  resolveClientPfmLineage,
} from "../pfm-version-label.ts";
import {
  buildMindmapCacheKey,
  buildScopeKey,
  buildDrillPathFromParents,
  collapseConsecutiveDrillPath,
  collapseDuplicateHubInNodeKey,
  collectCenterHubKeysFromParentMap,
  filterInternalHubFromDrillPath,
  resolveCommittedNodeKeyInTree,
  stripPfmDisplayTitle,
  type DrillAncestor,
} from "../pfm-drill-keys.ts";
import "./../components/split-panel-layout.ts";
import "./../components/chat-panel.ts";
import "./../components/pfm-mindmap-mermaid.ts";
import "./../components/pfm-interactive-learning-toggle.ts";
import "./../components/pfm-node-report-panel.ts";
import "./../components/ai-answer-language-field.ts";
import {
  notifyOperator,
} from "../operator-notify.js";
import { t, type UiLocale } from "../i18n.ts";

type DrillLevel = {
  nodeKey: string;
  title: string;
  parents: DrillAncestor[];
};

type RunScreenshot = {
  fingerprint: string;
  url: string;
  thumbnailUrl: string;
  caption: string;
  timestamp: number;
  relatedStepId: string;
};

type RunActivity = {
  fingerprint: string;
  timestamp: number;
  kind: string;
  text: string;
  operatorPhase: OperatorActivityPhase | "";
  relatedStepId: string;
  screenshots: RunScreenshot[];
};

type FullscreenActivityImage = {
  url: string;
  caption: string;
  activityText: string;
  timestamp: number;
};

type RunLeftTab = "explore" | "activity";
type RunConfirmAction = "delete" | "invalidate" | "finish" | "cancel";

type RunsViewState = {
  tabByRun?: Record<string, RunLeftTab>;
  scrollByRunTab?: Record<string, number>;
};

const RUNS_VIEW_STATE_KEY = "ead-expui.runs-view-state.v1";

function loadRunsViewState(): RunsViewState {
  try {
    return JSON.parse(localStorage.getItem(RUNS_VIEW_STATE_KEY) || "{}") as RunsViewState;
  } catch {
    return {};
  }
}

function saveRunsViewState(next: RunsViewState): void {
  try {
    localStorage.setItem(RUNS_VIEW_STATE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function isRunLeftTab(value: unknown): value is RunLeftTab {
  return value === "explore" || value === "activity";
}

const STATUS_COLORS: Record<string, string> = {
  pending: "#8b8d93",
  running: "#6ea8fe",
  Initializing: "#8b8d93",
  "Initializing...": "#8b8d93",
  "Exploring...": "#6ea8fe",
  "Running...": "#6ea8fe",
  "Reporting...": "#ab47bc",
  Done: "#4caf50",
  completed: "#4caf50",
  abandoned: "#f59e0b",
  Abandoned: "#f59e0b",
  "AI Finished": "#4caf50",
  "AI Abandoned": "#f59e0b",
  "AI Canceled": "#f59e0b",
  failed: "#f44336",
  cancelled: "#ff9800",
  error: "#f44336",
  reporting: "#ab47bc",
  "User Finish - Done": "#22c55e",
  "User Finish - Canceled": "#f59e0b",
  Success: "#4caf50",
  Failed: "#f44336",
  "No Run": "#8b8d93",
};

@customElement("ead-runs-view")
export class EadRunsView extends LitElement {
  @property({ type: String }) locale: UiLocale = "en";

  private _t(key: string, params?: Record<string, string | number>): string {
    return t(key, params, this.locale);
  }

  @property({ type: Boolean, attribute: "force-readonly-chat" })
  forceReadonlyChat = false;
  @property({ type: Boolean, attribute: "hide-chat-panel" })
  hideChatPanel = false;

  static styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .status-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: var(--radius-full, 9999px);
      font-size: 10px;
      font-weight: 600;
      text-transform: capitalize;
    }

    .btn {
      padding: 5px 10px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      font-size: 11px;
      font-weight: 500;
      cursor: pointer;
    }

    .btn:hover {
      background: var(--bg-hover);
    }

    .btn-sm {
      padding: 4px 8px;
      font-size: 11px;
    }

    .crumb {
      padding: 4px 8px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 76%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 86%, transparent);
      color: var(--text-strong, #f4f4f5);
      font-size: 11px;
      cursor: pointer;
    }

    .crumb:hover {
      background: var(--bg-hover, #232733);
    }

    .crumb--active {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 64%, transparent);
      background: color-mix(in srgb, var(--accent, #6ea8fe) 18%, transparent);
      color: color-mix(in srgb, var(--text-strong, #f4f4f5) 94%, #dbe6ff);
      font-weight: 700;
    }

    .crumb--visited-dim {
      opacity: 0.52;
    }

    .mindmap-header {
      margin-bottom: 8px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 10px;
      font-size: 12px;
    }

    .mindmap-header__crumbs {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      min-width: 0;
      flex: 1;
    }

    .pfm-explore-stack {
      margin-bottom: 20px;
    }

    .pfm-explore-stack--maximized {
      position: fixed;
      inset: 14px;
      z-index: 2600;
      display: flex;
      flex-direction: column;
      margin-bottom: 0;
      padding: 14px 16px;
      border-radius: 12px;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      background: color-mix(in srgb, var(--card, #161920) 96%, #0b0e14 4%);
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
    }

    .pfm-explore-stack__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;
      flex-shrink: 0;
      margin-bottom: 8px;
    }

    .pfm-explore-stack__header .section-title {
      margin: 0;
      min-width: 0;
    }

    .pfm-explore-stack__actions {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      margin-left: auto;
    }

    .pfm-explore-icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      cursor: pointer;
      padding: 0;
    }

    .pfm-explore-icon-btn:hover:not(:disabled) {
      background: var(--bg-hover, #232733);
    }

    .pfm-explore-icon-btn > span {
      display: inline-flex;
      width: 16px;
      height: 16px;
    }

    .pfm-explore-icon-btn svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }

    .pfm-explore-stack__body {
      display: flex;
      flex-direction: column;
      gap: 0;
      min-height: 0;
    }

    .pfm-explore-stack--maximized .pfm-explore-stack__body {
      flex: 1;
      overflow: auto;
    }

    .pfm-explore-stack--maximized .pfm-explore-stack__mindmap-host {
      min-height: min(52vh, 520px);
      flex-shrink: 0;
    }

    .pfm-explore-stack__mindmap-host {
      margin-bottom: 0;
    }

    .pfm-explore-stack__report-host .section-block {
      margin-bottom: 0;
    }

    .btn-ghost {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .btn-danger {
      color: var(--danger, #ef4444);
      border-color: color-mix(in srgb, var(--danger) 35%, transparent);
    }

    .detail-panel {
      flex: 1 1 auto;
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      background: var(--bg, #0e1015);
    }

    .left-pane-slot {
      height: 100%;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .chat-slot {
      height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .chat-slot > ead-chat-panel {
      flex: 1 1 auto;
      min-height: 0;
    }

    .detail-header {
      position: sticky;
      top: 0;
      z-index: 12;
      padding: 10px 18px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      box-sizing: border-box;
      background: color-mix(in srgb, var(--bg, #0e1015) 94%, transparent);
      backdrop-filter: blur(10px);
    }

    .detail-header h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 650;
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      color: var(--text-strong);
    }

    .status-inline {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
      min-width: 0;
    }

    .detail-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;
      margin-left: auto;
    }

    .detail-refresh-btn {
      width: 30px;
      height: 30px;
      justify-content: center;
      padding: 0;
    }

    .status-timer {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px;
      border-radius: var(--radius-full, 9999px);
      font-size: 11px;
      font-weight: 650;
      letter-spacing: 0.02em;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 85%, transparent);
      color: var(--text-strong, #e4e4e7);
      font-variant-numeric: tabular-nums;
    }

    .status-timer--warn {
      border-color: color-mix(in srgb, var(--warn, #f59e0b) 45%, transparent);
      color: var(--warn-muted, #fcd34d);
    }

    .status-timer--critical {
      border-color: color-mix(in srgb, var(--danger, #ef4444) 45%, transparent);
      color: var(--danger-muted, #fca5a5);
    }

    .overflow-menu-panel {
      position: absolute;
      right: 0;
      top: calc(100% + 6px);
      min-width: 160px;
      border: 1px solid var(--border);
      border-radius: 8px;
      background: var(--bg-elevated, #1b1f2a);
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.28);
      overflow: hidden;
      z-index: 8;
    }

    .overflow-menu-panel button {
      display: block;
      width: 100%;
      text-align: left;
      border: none;
      border-radius: 0;
      background: transparent;
      padding: 8px 10px;
      font-size: 12px;
      color: var(--text);
      cursor: pointer;
    }

    .overflow-menu-panel button:hover:not(:disabled) {
      background: color-mix(in srgb, var(--bg-hover) 55%, transparent);
    }

    .overflow-menu-panel button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .overflow-menu-panel .overflow-menu-danger {
      color: var(--danger-muted, #fca5a5);
    }

    .detail-body {
      flex: 1 0 auto;
      padding: 8px 18px 24px;
      width: 100%;
      box-sizing: border-box;
    }

    .run-desc {
      font-size: 13px;
      line-height: 1.55;
      color: var(--muted);
      margin-bottom: 16px;
      max-width: 72ch;
    }

    .banner {
      padding: 10px 14px;
      border-radius: var(--radius-md, 10px);
      margin-bottom: 14px;
      font-size: 12px;
      line-height: 1.5;
      border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
    }

    .banner--paused {
      background: color-mix(in srgb, var(--accent-2-muted, rgb(245, 158, 11)) 14%, transparent);
      color: var(--text-strong);
    }

    .banner--error {
      background: color-mix(in srgb, var(--danger) 14%, transparent);
      color: var(--danger-muted, #fca5a5);
      border-color: color-mix(in srgb, var(--danger) 22%, transparent);
    }

    .banner--budget {
      background: color-mix(in srgb, var(--warn) 12%, transparent);
      color: var(--warn-muted, #fcd34d);
    }

    .banner--info {
      background: color-mix(in srgb, var(--accent, #6ea8fe) 12%, transparent);
      color: var(--fg, #e4e5e7);
    }

    .banner--warning {
      background: color-mix(in srgb, var(--warn, #f59e0b) 14%, transparent);
      color: var(--warn-muted, #fcd34d);
      border-color: color-mix(in srgb, var(--warn, #f59e0b) 28%, transparent);
    }

    .banner--success {
      background: color-mix(in srgb, var(--ok, #22c55e) 12%, transparent);
      color: color-mix(in srgb, var(--ok, #22c55e) 82%, var(--fg, #e4e5e7));
      border-color: color-mix(in srgb, var(--ok, #22c55e) 24%, transparent);
    }

    .banner__title {
      display: block;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .banner__detail {
      display: block;
      margin-top: 4px;
      opacity: 0.92;
      font-size: 11px;
    }

    .banner--invalid {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      background: color-mix(in srgb, var(--fg, #e4e5e7) 6%, transparent);
      color: var(--fg, #e4e5e7);
      border-color: color-mix(in srgb, var(--border) 70%, transparent);
    }

    .banner--invalid svg {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
      margin-top: 1px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }

    .confirm-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1200;
      display: grid;
      place-items: center;
      padding: 24px;
      background: rgba(0, 0, 0, 0.58);
      backdrop-filter: blur(8px);
    }

    .confirm-card {
      width: min(520px, calc(100vw - 40px));
      border: 1px solid color-mix(in srgb, var(--danger, #ef4444) 28%, var(--border));
      border-radius: 16px;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--danger, #ef4444) 14%, transparent), transparent 38%),
        var(--bg-elevated, #161920);
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.48);
      overflow: hidden;
    }

    .confirm-card--finish {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 32%, var(--border));
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--accent, #6ea8fe) 18%, transparent), transparent 42%),
        var(--bg-elevated, #161920);
    }

    .confirm-icon--finish {
      background: color-mix(in srgb, var(--accent, #6ea8fe) 18%, transparent);
      color: var(--accent, #6ea8fe);
      font-size: 18px;
    }

    .confirm-note {
      margin-top: 10px;
      padding: 10px 12px;
      border-radius: 10px;
      background: color-mix(in srgb, var(--accent, #6ea8fe) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--accent, #6ea8fe) 22%, transparent);
      font-size: 12px;
      line-height: 1.55;
      color: var(--text);
    }

    .confirm-header {
      display: flex;
      gap: 12px;
      padding: 18px 20px 12px;
      align-items: flex-start;
    }

    .confirm-icon {
      width: 34px;
      height: 34px;
      border-radius: 12px;
      display: grid;
      place-items: center;
      background: color-mix(in srgb, var(--danger, #ef4444) 16%, transparent);
      color: var(--danger, #ef4444);
      font-weight: 800;
      flex: 0 0 auto;
    }

    .confirm-title {
      margin: 0;
      font-size: 16px;
      color: var(--text-strong, #f4f4f5);
      font-weight: 750;
    }

    .confirm-subtitle {
      margin-top: 5px;
      font-size: 12px;
      color: var(--muted, #8b8d93);
      line-height: 1.5;
    }

    .confirm-body {
      padding: 0 20px 16px 66px;
      display: grid;
      gap: 10px;
      color: var(--text, #d4d4d8);
      font-size: 13px;
      line-height: 1.55;
    }

    .confirm-warning {
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid color-mix(in srgb, var(--warn, #f59e0b) 28%, transparent);
      background: color-mix(in srgb, var(--warn, #f59e0b) 11%, transparent);
      color: var(--warn-muted, #fcd34d);
    }

    .confirm-error {
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid color-mix(in srgb, var(--danger, #ef4444) 30%, transparent);
      background: color-mix(in srgb, var(--danger, #ef4444) 12%, transparent);
      color: var(--danger-muted, #fca5a5);
    }

    .confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 14px 20px 18px;
      border-top: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      background: color-mix(in srgb, var(--bg, #0e1015) 42%, transparent);
    }

    .section-block {
      margin-bottom: 20px;
    }

    .left-tabs {
      display: inline-flex;
      gap: 6px;
      padding: 4px;
      border: 1px solid color-mix(in srgb, var(--border) 76%, transparent);
      border-radius: 10px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 96%, #000);
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
    }

    .left-tab {
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      color: var(--muted);
      font-size: 12px;
      font-weight: 600;
      padding: 6px 10px;
      cursor: pointer;
    }

    .left-tab:hover {
      color: var(--text-strong);
      background: color-mix(in srgb, var(--bg-hover, #232733) 72%, transparent);
    }

    .left-tab--active {
      color: var(--text-strong);
      background: color-mix(in srgb, var(--accent, #6ea8fe) 16%, transparent);
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 46%, transparent);
    }

    .activity-tab-only {
      margin-top: 4px;
    }

    .activity-tab-hint {
      margin: 0 0 12px;
      font-size: 12px;
      color: var(--muted);
      line-height: 1.45;
    }

    .node-learning-card {
      margin: 0 0 12px;
      padding: 10px 12px;
      border: 1px solid color-mix(in srgb, var(--border) 74%, transparent);
      border-radius: var(--radius-md, 10px);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 90%, transparent);
    }

    .node-learning-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 8px;
    }

    .node-learning-title {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-strong, #f4f4f5);
    }

    .node-learning-trend {
      font-size: 11px;
      color: var(--muted);
      white-space: nowrap;
    }

    .node-learning-metrics {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 6px;
    }

    .node-learning-chip {
      font-size: 11px;
      line-height: 1.35;
      padding: 4px 8px;
      border-radius: 999px;
      border: 1px solid color-mix(in srgb, var(--border) 68%, transparent);
      background: color-mix(in srgb, var(--card, #1b2030) 88%, transparent);
      color: var(--text, #e7e7ea);
    }

    .node-learning-totals {
      font-size: 11px;
      color: var(--muted);
      line-height: 1.45;
    }

    .activity-value-row {
      margin-top: 6px;
      font-size: 12px;
      line-height: 1.5;
      color: var(--text);
    }

    .activity-value-label {
      font-weight: 600;
      color: var(--muted);
      margin-right: 6px;
    }

    .activity-missing-evidence {
      margin-top: 6px;
      font-size: 11px;
      color: #e8a87c;
    }

    .activity-empty {
      padding: 28px 20px;
      text-align: center;
      border: 1px dashed color-mix(in srgb, var(--border) 55%, transparent);
      border-radius: var(--radius-md, 10px);
      color: var(--muted);
      font-size: 13px;
      line-height: 1.65;
    }

    .activity-empty-title {
      font-weight: 600;
      color: var(--text);
      margin-bottom: 8px;
    }

    .activity-empty p {
      margin: 0;
    }

    .ss-layout {
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      border-radius: var(--radius-md, 10px);
      background: color-mix(in srgb, var(--card, #161920) 88%, transparent);
      overflow: hidden;
    }

    .activity-feed {
      overflow: visible;
      padding: 12px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 84%, transparent);
    }

    .activity-row {
      padding: 8px 6px;
      border-bottom: 1px dashed color-mix(in srgb, var(--border) 56%, transparent);
    }

    .activity-row:last-child {
      border-bottom: none;
    }

    .activity-view-more {
      display: block;
      width: 100%;
      margin-top: 10px;
      padding: 8px 0 2px;
      border: none;
      background: transparent;
      text-align: center;
      font-size: 12px;
      font-weight: 500;
      color: color-mix(in srgb, var(--accent, #a78bfa) 88%, white);
      cursor: pointer;
    }

    .activity-view-more:hover {
      text-decoration: underline;
    }

    .activity-time {
      font-size: 11px;
      color: var(--muted);
      margin-bottom: 4px;
      font-variant-numeric: tabular-nums;
    }

    .activity-text {
      font-size: 13px;
      line-height: 1.5;
      color: var(--text);
      word-break: break-word;
    }

    .activity-prose {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .activity-prose p {
      margin: 0;
      white-space: pre-wrap;
    }

    .activity-inline-thumb--broken {
      display: none;
    }

    .activity-title-body {
      display: block;
    }

    .activity-inline-images {
      margin-top: 8px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 8px;
    }

    .activity-inline-thumb {
      position: relative;
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 8px;
      overflow: hidden;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 86%, transparent);
    }

    .activity-inline-thumb-main {
      border: 0;
      padding: 0;
      cursor: pointer;
      text-align: left;
      color: inherit;
      background: transparent;
    }

    .activity-inline-thumb > .activity-inline-thumb-main {
      width: 100%;
    }

    .activity-thumb-image-wrap {
      position: relative;
      display: inline-block;
      max-width: 100%;
      background: #0f1320;
      vertical-align: top;
    }

    .activity-inline-thumb--expanded {
      grid-column: 1 / -1;
    }

    .activity-inline-thumb-main img {
      width: auto;
      max-width: 100%;
      height: 96px;
      object-fit: contain;
      display: block;
    }

    .activity-inline-thumb--expanded .activity-thumb-image-wrap,
    .activity-inline-thumb--expanded .activity-inline-thumb-main {
      display: block;
      width: 100%;
    }

    .activity-inline-thumb--expanded .activity-inline-thumb-main img {
      width: 100%;
      height: auto;
      max-height: 520px;
      object-fit: contain;
    }

    .activity-inline-thumb .ss-thumb-time {
      min-height: auto;
      -webkit-line-clamp: 2;
    }

    .activity-fullscreen-btn {
      position: absolute;
      top: 6px;
      right: 6px;
      z-index: 1;
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid color-mix(in srgb, white 28%, transparent);
      border-radius: 999px;
      color: #fff;
      background: color-mix(in srgb, #05070d 78%, transparent);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.34);
      cursor: pointer;
      opacity: 0;
      transform: translateY(-2px);
      transition:
        opacity 0.16s ease,
        transform 0.16s ease,
        background 0.16s ease;
    }

    .activity-inline-thumb--expanded .activity-fullscreen-btn {
      top: 10px;
      right: 10px;
      width: 30px;
      height: 30px;
    }

    .activity-inline-thumb:hover .activity-fullscreen-btn,
    .activity-fullscreen-btn:focus-visible {
      opacity: 1;
      transform: translateY(0);
    }

    .activity-fullscreen-btn:hover,
    .activity-fullscreen-btn:focus-visible {
      background: color-mix(in srgb, var(--accent, #7c3aed) 76%, #05070d);
    }

    .activity-fullscreen-btn svg {
      width: 13px;
      height: 13px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .activity-inline-thumb--expanded .activity-fullscreen-btn svg {
      width: 16px;
      height: 16px;
    }

    .activity-fullscreen-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 28px;
      background: rgba(4, 6, 12, 0.92);
      backdrop-filter: blur(8px);
    }

    .activity-fullscreen-toolbar {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: flex-start;
      color: var(--text, #f8fafc);
    }

    .activity-fullscreen-meta {
      min-width: 0;
      display: grid;
      gap: 4px;
      font-size: 13px;
      line-height: 1.45;
    }

    .activity-fullscreen-time {
      color: var(--muted, #94a3b8);
      font-size: 12px;
      font-variant-numeric: tabular-nums;
    }

    .activity-fullscreen-close {
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid color-mix(in srgb, white 22%, transparent);
      border-radius: 999px;
      color: #fff;
      background: color-mix(in srgb, #111827 72%, transparent);
      cursor: pointer;
      flex: 0 0 auto;
    }

    .activity-fullscreen-close svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .activity-fullscreen-stage {
      flex: 1;
      min-height: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid color-mix(in srgb, white 12%, transparent);
      border-radius: 16px;
      background: rgba(0, 0, 0, 0.28);
      overflow: hidden;
    }

    .activity-fullscreen-stage img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .activity-fullscreen-hint {
      color: var(--muted, #94a3b8);
      font-size: 12px;
      text-align: center;
    }

    .ss-meta {
      margin-top: 8px;
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: 11px;
      color: var(--muted);
    }

    .ss-caption {
      margin-top: 8px;
      font-size: 12px;
      color: var(--text);
      line-height: 1.5;
    }

    .ss-strip {
      padding: 10px;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 8px;
      max-height: 240px;
      overflow: auto;
    }

    .ss-thumb {
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 8px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 86%, transparent);
      cursor: pointer;
      overflow: hidden;
      padding: 0;
      text-align: left;
    }

    .ss-thumb:hover {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 54%, transparent);
    }

    .ss-thumb--active {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 70%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent, #6ea8fe) 40%, transparent);
    }

    .ss-thumb img {
      width: 100%;
      height: 96px;
      object-fit: contain;
      display: block;
      background: #0f1320;
    }

    .ss-thumb-time {
      padding: 5px 7px;
      font-size: 10px;
      color: var(--muted);
      line-height: 1.3;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      min-height: calc(1.3em * 3);
    }

    .node-report-panel {
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      border-radius: var(--radius-md, 10px);
      background: color-mix(in srgb, var(--card, #161920) 86%, transparent);
      overflow: hidden;
    }

    .node-report-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 14px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
    }

    .node-report-title {
      font-size: 13px;
      font-weight: 700;
      color: var(--text-strong);
    }

    .node-report-meta {
      margin-top: 3px;
      font-size: 11px;
      color: var(--muted);
    }

    .node-report-body {
      padding: 14px;
      font-size: 13px;
      line-height: 1.55;
      color: var(--text);
      overflow-wrap: anywhere;
    }

    .node-report-body :is(h1, h2, h3, h4) {
      margin: 16px 0 8px;
      color: var(--text-strong);
      line-height: 1.28;
    }

    .node-report-body h1 {
      font-size: 16px;
    }

    .node-report-body h2 {
      font-size: 14px;
      padding-bottom: 5px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
    }

    .node-report-body h3 {
      font-size: 13px;
      margin-top: 18px;
      padding: 8px 10px;
      border-radius: 8px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 88%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
    }

    .node-report-body h4 {
      font-size: 12px;
      color: var(--muted);
    }

    .node-report-body :is(ul, ol) {
      padding-left: 18px;
      margin: 6px 0 10px;
    }

    .node-report-body li {
      margin: 4px 0;
    }

    .node-report-body p {
      margin: 7px 0 10px;
    }

    .node-report-body table {
      display: block;
      width: 100%;
      overflow-x: auto;
      border-collapse: collapse;
      font-size: 12px;
    }

    .node-report-body :is(th, td) {
      border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
      padding: 6px 8px;
      vertical-align: top;
    }

    .section-title {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 10px;
    }

    .section-title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 10px;
    }

    .section-title-row .section-title {
      margin-bottom: 0;
    }

    .section-toggle-btn {
      width: 24px;
      height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #161920) 86%, transparent);
      color: var(--text, #e8e8ea);
      cursor: pointer;
      padding: 0;
    }

    .section-toggle-btn:hover {
      background: var(--bg-hover, #232733);
    }

    .section-toggle-btn svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .kv-grid {
      display: grid;
      gap: 8px 16px;
      font-size: 13px;
      color: var(--text);
      line-height: 1.5;
    }

    .kv-grid dt {
      margin: 0;
      font-weight: 600;
      color: var(--muted);
      font-size: 12px;
    }

    .kv-grid dd {
      margin: 0;
    }

    .stats-row {
      display: flex;
      gap: 10px;
      margin-bottom: 18px;
      flex-wrap: wrap;
    }

    .stat {
      padding: 8px 12px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      background: color-mix(in srgb, var(--card, #161920) 88%, transparent);
      min-width: 72px;
    }

    .stat .label {
      font-size: 10px;
      color: var(--muted);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .stat .val {
      font-size: 15px;
      font-weight: 650;
      color: var(--text-strong);
      margin-top: 2px;
    }

    .pfm-tree {
      font-family: var(--mono, ui-monospace, monospace);
      font-size: 12px;
      line-height: 1.9;
    }

    .pfm-node-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 2px 0;
    }

    .pfm-title {
      color: var(--text);
      font-weight: 500;
    }

    .pfm-status {
      font-size: 10px;
      padding: 1px 6px;
      border-radius: var(--radius-full);
    }

    .pfm-children {
      margin-left: 20px;
      border-left: 1px dashed color-mix(in srgb, var(--border) 70%, transparent);
      padding-left: 10px;
    }

    .pfm-case .pfm-title {
      font-weight: 400;
      color: color-mix(in srgb, var(--text-strong) 90%, transparent);
      font-size: 12px;
    }

    .pfm-step {
      color: var(--muted);
      font-size: 12px;
    }

    .progress-log {
      margin-top: 8px;
    }

    .log-entry {
      padding: 4px 0;
      font-size: 12px;
      display: flex;
      gap: 8px;
    }

    .log-entry .log-time {
      color: var(--muted);
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }

    .log-entry .log-text {
      color: var(--text);
    }

    .log-entry.log-tool .log-text {
      color: var(--accent-2, #14b8a6);
    }

    .log-entry.log-system .log-text {
      color: var(--warn-muted, #fbbf24);
    }

    .log-entry--pinned {
      padding: 8px 10px;
      border: 1px solid color-mix(in srgb, var(--accent-2, #14b8a6) 30%, transparent);
      border-radius: 8px;
      background: color-mix(in srgb, var(--accent-2, #14b8a6) 10%, transparent);
      margin-bottom: 8px;
    }

    .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      min-height: 180px;
      color: var(--muted);
      flex-direction: column;
      gap: 8px;
      font-size: 13px;
      text-align: center;
      padding: 24px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: var(--muted);
    }

    .live-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--ok, #22c55e);
      animation: pulse 1.5s infinite;
      flex-shrink: 0;
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.35;
      }
    }
  `;

  @state() private _splitRatio = 0.5;
  @state() private _stopMenuOpenFor: string | null = null;
  @state() private _leftTab: RunLeftTab = "explore";
  @state() private _contextPath: DrillAncestor[] = [];
  @state() private _activeContextDepth = 0;
  /** Bumps when visited-node localStorage changes so mindmap highlights refresh. */
  @state() private _pfmVisitEpoch = 0;
  @state() private _selectedNode: DrillLevel | null = null;
  @state() private _selectedNodeReportCollapsed = false;
  @state() private _activeScreenshotFingerprint = "";
  @state() private _expandedActivityImageKey = "";
  @state() private _fullscreenActivityImage: FullscreenActivityImage | null = null;
  @state() private _pfmExploreMaximized = false;
  @state() private _confirmRunAction: { action: RunConfirmAction; run: ProjectExecute } | null = null;
  @state() private _confirmRunError = "";
  @state() private _confirmRunBusy = false;
  /** Set while cancel/finish API is in flight; modal closes immediately. */
  @state() private _stoppingRunId: string | null = null;
  @state() private _extendingRunId: string | null = null;
  @state() private _stopRunError = "";
  @state() private _headerClockTick = 0;
  @state() private _runOverflowMenuFor: string | null = null;
  @state() private _answerLanguageUpdatingRunId: string | null = null;
  @state() private _brokenActivityImageKeys = new Set<string>();
  @state() private _activityImageFallbackSrc = new Map<string, string>();
  @state() private _dbActivities: RunActivity[] = [];
  @state() private _dbActivitiesRunId = "";
  @state() private _dbActivitiesLoaded = false;
  private _activityLogFetchTimer: ReturnType<typeof setTimeout> | null = null;
  private _activityLogPollInterval: ReturnType<typeof setInterval> | null = null;
  private _activityLogLastFetchAt = 0;
  private _activityLogInFlight = false;
  private _dbActivitiesMaxSeq = 0;
  @state() private _deliveryValidation: PfmDeliveryValidationPayload | null = null;
  @state() private _deliveryValidationRunId = "";
  private _deliveryValidationPollInterval: ReturnType<typeof setInterval> | null = null;
  private _deliveryValidationInFlight = false;
  private _activeRunTicker: ReturnType<typeof setInterval> | null = null;
  private _unsubscribeStore: (() => void) | null = null;
  private _previousSelectedExecutionId: string | null = null;
  private _lastSeenPfmRevisionForRun = 0;
  private _lastSeenPfmRevisionRunId = "";
  private _lastSeenHasDeliveredForRun = false;
  private _lastSeenHasDeliveredRunId = "";
  private _detailRenderRecoveryRunId = "";
  private _screenshotSelectionByExecution = new Map<string, string>();
  private _restoreScrollAfterUpdate = false;

  connectedCallback(): void {
    super.connectedCallback();
    this._unsubscribeStore = executionsStore.subscribe(() => this._handleStoreChange());
    window.addEventListener("keydown", this._handleWindowKeydown);
    const ex = executionsStore.selectedExecution;
    if (ex?.id && !this._pfmLineageHasVersion(ex)) {
      void executionsStore.refreshSelectionDetail();
    }
    this._syncDeliveryValidationPoll();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._activeRunTicker != null) {
      window.clearInterval(this._activeRunTicker);
      this._activeRunTicker = null;
    }
    if (this._activityLogFetchTimer != null) {
      window.clearTimeout(this._activityLogFetchTimer);
      this._activityLogFetchTimer = null;
    }
    if (this._activityLogPollInterval != null) {
      window.clearInterval(this._activityLogPollInterval);
      this._activityLogPollInterval = null;
    }
    if (this._deliveryValidationPollInterval != null) {
      window.clearInterval(this._deliveryValidationPollInterval);
      this._deliveryValidationPollInterval = null;
    }
    this._unsubscribeStore?.();
    this._unsubscribeStore = null;
    window.removeEventListener("keydown", this._handleWindowKeydown);
  }

  private _handleWindowKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    if (this._pfmExploreMaximized) {
      event.preventDefault();
      this._pfmExploreMaximized = false;
      return;
    }
    if (this._fullscreenActivityImage) {
      event.preventDefault();
      this._fullscreenActivityImage = null;
      return;
    }
    if (this._runOverflowMenuFor) {
      this._runOverflowMenuFor = null;
      return;
    }
    if (this._stopMenuOpenFor) {
      this._stopMenuOpenFor = null;
      return;
    }
    if (!this._expandedActivityImageKey) return;
    event.preventDefault();
    this._expandedActivityImageKey = "";
  };

  protected override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(changedProperties);
    if (this._restoreScrollAfterUpdate) {
      this._restoreScrollAfterUpdate = false;
      requestAnimationFrame(() => this._restoreDetailScroll());
    }
    this._syncActiveRunTicker();
    if (changedProperties.has("_leftTab")) {
      this._syncActivityLogPoll();
    }
  }

  private _syncActiveRunTicker(): void {
    const ex = executionsStore.selectedExecution;
    const live = Boolean(ex && isLiveProjectExecute(ex));
    const hasBudget = Number(ex?.timeBudgetMinutes || 0) > 0;
    if (live && hasBudget) {
      if (this._activeRunTicker == null) {
        this._activeRunTicker = window.setInterval(() => {
          this._headerClockTick += 1;
        }, 1000);
      }
    } else if (this._activeRunTicker != null) {
      window.clearInterval(this._activeRunTicker);
      this._activeRunTicker = null;
    }
  }

  private _runScrollKey(runId: string, tab: RunLeftTab = this._leftTab): string {
    return `${runId}:${tab}`;
  }

  private _persistLeftTab(runId: string, tab: RunLeftTab): void {
    const state = loadRunsViewState();
    saveRunsViewState({
      ...state,
      tabByRun: {
        ...(state.tabByRun || {}),
        [runId]: tab,
      },
    });
  }

  private _persistDetailScroll(runId: string, tab: RunLeftTab, scrollTop: number): void {
    const state = loadRunsViewState();
    saveRunsViewState({
      ...state,
      scrollByRunTab: {
        ...(state.scrollByRunTab || {}),
        [this._runScrollKey(runId, tab)]: Math.max(0, Math.round(scrollTop)),
      },
    });
  }

  private _loadLeftTab(runId: string | null): RunLeftTab {
    if (!runId) return "explore";
    const value = loadRunsViewState().tabByRun?.[runId];
    return isRunLeftTab(value) ? value : "explore";
  }

  private _loadDetailScroll(runId: string, tab: RunLeftTab): number {
    const value = loadRunsViewState().scrollByRunTab?.[this._runScrollKey(runId, tab)] ?? 0;
    return Number.isFinite(value) ? Math.max(0, Number(value)) : 0;
  }

  private _setLeftTab(tab: RunLeftTab): void {
    const id = executionsStore.selectedExecution?.id;
    if (id) this._persistLeftTab(id, tab);
    this._leftTab = tab;
    this._restoreScrollAfterUpdate = true;
    this._syncActivityLogPoll();
    this._syncDeliveryValidationPoll();
    if (tab === "explore") {
      this._scheduleExploreMindmapLoad();
    }
    if (tab === "activity") {
      const selected = executionsStore.selectedExecution;
      if (selected?.id) {
        this._scheduleDbActivityLogFetch(selected, { immediate: true });
      }
    }
  }

  private _shouldPollActivityLog(): boolean {
    return this._leftTab === "activity";
  }

  private _syncActivityLogPoll(): void {
    if (this._activityLogPollInterval != null) {
      window.clearInterval(this._activityLogPollInterval);
      this._activityLogPollInterval = null;
    }
    if (!this._shouldPollActivityLog()) return;
    const ex = executionsStore.selectedExecution;
    if (!ex?.id) return;
    const ms = isLiveProjectExecute(ex) ? 15_000 : 30_000;
    this._activityLogPollInterval = window.setInterval(() => {
      if (!this._shouldPollActivityLog() || !canPollGateway()) return;
      const current = executionsStore.selectedExecution;
      if (!current?.id) return;
      this._scheduleDbActivityLogFetch(current);
    }, ms);
  }

  private _shouldPollDeliveryValidation(ex: ProjectExecute | null | undefined): boolean {
    if (!ex?.id || this._leftTab !== "explore") return false;
    return shouldPollPfmDeliveryValidation({
      isLiveRun: isLiveProjectExecute(ex),
      loginSuccessful: this._loginPhaseStatus(ex) === "success",
      projectRunStatus: readProjectRunStatus(ex),
    });
  }

  private _syncDeliveryValidationPoll(): void {
    if (this._deliveryValidationPollInterval != null) {
      window.clearInterval(this._deliveryValidationPollInterval);
      this._deliveryValidationPollInterval = null;
    }
    const ex = executionsStore.selectedExecution;
    if (!this._shouldPollDeliveryValidation(ex)) {
      if (!ex?.id || ex.id !== this._deliveryValidationRunId) {
        this._deliveryValidation = null;
        this._deliveryValidationRunId = "";
      }
      return;
    }
    void this._fetchDeliveryValidation(ex!);
    this._deliveryValidationPollInterval = window.setInterval(() => {
      const current = executionsStore.selectedExecution;
      if (!this._shouldPollDeliveryValidation(current) || !canPollGateway()) return;
      void this._fetchDeliveryValidation(current!);
    }, 12_000);
  }

  private async _fetchDeliveryValidation(ex: ProjectExecute): Promise<void> {
    const runId = String(ex.id || "").trim();
    if (!runId || !canPollGateway()) return;
    if (this._deliveryValidationInFlight) return;
    this._deliveryValidationInFlight = true;
    try {
      const payload = await hermesClient.request<PfmDeliveryValidationPayload>(
        "pfm.deliveryValidation",
        { executionId: runId },
      );
      if (!payload || executionsStore.selectedExecution?.id !== runId) return;
      this._deliveryValidation = payload;
      this._deliveryValidationRunId = runId;
      this.requestUpdate();
    } catch {
      // Non-fatal — banner stays on last known state.
    } finally {
      this._deliveryValidationInFlight = false;
    }
  }

  private _handleDetailScroll(event: Event): void {
    const id = executionsStore.selectedExecution?.id;
    if (!id) return;
    this._persistDetailScroll(id, this._leftTab, (event.currentTarget as HTMLElement).scrollTop);
  }

  private _restoreDetailScroll(): void {
    const id = executionsStore.selectedExecution?.id;
    if (!id) return;
    const panel = this.shadowRoot?.querySelector(".detail-panel") as HTMLElement | null;
    if (!panel) return;
    panel.scrollTop = this._loadDetailScroll(id, this._leftTab);
  }

  private _formatClockTime(ts: number): string {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  private _handleStoreChange(): void {
    // Reset drill chain when the user switches to a different run.
    const nextId = executionsStore.selectedExecution?.id ?? null;
    const runChanged = nextId !== this._previousSelectedExecutionId;
    if (runChanged) {
      this._previousSelectedExecutionId = nextId;
      queueMicrotask(() => {
        this._lastSeenPfmRevisionForRun = 0;
        this._lastSeenPfmRevisionRunId = "";
        this._lastSeenHasDeliveredForRun = false;
        this._lastSeenHasDeliveredRunId = "";
        this._brokenActivityImageKeys = new Set();
        this._activityImageFallbackSrc = new Map();
        this._dbActivities = [];
        this._dbActivitiesRunId = "";
        this._dbActivitiesLoaded = false;
        this._dbActivitiesMaxSeq = 0;
        this._deliveryValidation = null;
        this._deliveryValidationRunId = "";
        this._stopMenuOpenFor = null;
        this._runOverflowMenuFor = null;
        this._stoppingRunId = null;
        this._extendingRunId = null;
        this._stopRunError = "";
        this._leftTab = this._loadLeftTab(nextId);
        if (this._contextPath.length) this._contextPath = [];
        this._activeContextDepth = 0;
        if (this._selectedNode) this._selectedNode = null;
        if (this._expandedActivityImageKey) this._expandedActivityImageKey = "";
        if (this._fullscreenActivityImage) this._fullscreenActivityImage = null;
        if (this._pfmExploreMaximized) this._pfmExploreMaximized = false;
        this._restoreScrollAfterUpdate = true;
        const selected = executionsStore.selectedExecution;
        if (selected?.id && !this._pfmLineageHasVersion(selected)) {
          void executionsStore.refreshSelectionDetail();
        }
        if (selected?.id) {
          mergeRunActivitiesPersisted(selected.id, this._extractRunActivities(selected));
          if (this._shouldPollActivityLog()) {
            this._scheduleDbActivityLogFetch(selected, { immediate: true });
          }
        }
        this._syncActivityLogPoll();
        this._syncDeliveryValidationPoll();
        this._maybeReloadMindmapOnPfmRevisionBump(selected, true);
        this._syncScreenshotSelection(selected, true);
        this._scheduleExploreMindmapLoad();
        this.requestUpdate();
      });
      return;
    }
    const selected = executionsStore.selectedExecution;
    if (selected?.id) {
      mergeRunActivitiesPersisted(selected.id, this._extractRunActivities(selected));
    }
    this._maybeReloadMindmapOnPfmRevisionBump(selected, false);
    this._syncDeliveryValidationPoll();
    this._syncScreenshotSelection(selected, false);
    this.requestUpdate();
  }

  /** Reload Explore mindmap when poll bumps pfmRevision (delivery autosave on this run). */
  private _maybeReloadMindmapOnPfmRevisionBump(
    ex: ProjectExecute | null | undefined,
    runChanged: boolean,
  ): void {
    const runId = String(ex?.id || "").trim();
    if (!runId || !ex) return;
    const rev = Math.max(0, Number(ex?.pfmRevision ?? 0));
    const hasDelivered = ex.hasDeliveredPfmMapReport === true;
    const displayCurrent = String(ex.pfmMapDisplayMode || "").trim().toLowerCase() === "current";
    const hasOwnDbMap =
      hasDelivered
      || ex.pfmHasCommittedSnapshot === true
      || (displayCurrent && rev > 0);
    const treeRead = this._resolvedPfmTreeReadExecutionId(ex);
    const deliveryJustFlipped =
      hasDelivered
      && (runId !== this._lastSeenHasDeliveredRunId || !this._lastSeenHasDeliveredForRun);
    if (deliveryJustFlipped) {
      this._lastSeenHasDeliveredRunId = runId;
      this._lastSeenHasDeliveredForRun = true;
      this._scheduleExploreMindmapLoad();
    }
    if (!isLiveProjectExecute(ex)) {
      this._lastSeenPfmRevisionRunId = runId;
      this._lastSeenPfmRevisionForRun = rev;
      if (hasOwnDbMap) {
        this._queueMindmapSyncFromDb();
      }
      return;
    }
    // Pre-delivery: reading baseline — skip sync unless delivery just flipped or rev bumped on own map.
    if (treeRead && treeRead !== runId && !hasDelivered && !deliveryJustFlipped) {
      if (runChanged || runId !== this._lastSeenPfmRevisionRunId) {
        this._lastSeenPfmRevisionRunId = runId;
        this._lastSeenPfmRevisionForRun = rev;
      }
      return;
    }
    if (runChanged || runId !== this._lastSeenPfmRevisionRunId) {
      this._lastSeenPfmRevisionRunId = runId;
      this._lastSeenPfmRevisionForRun = rev;
      if (hasOwnDbMap) {
        this._queueMindmapSyncFromDb();
      }
      return;
    }
    const prev = this._lastSeenPfmRevisionForRun;
    this._lastSeenPfmRevisionForRun = rev;
    if (rev <= prev && !deliveryJustFlipped) return;
    if (this._shouldPollActivityLog()) {
      this._scheduleDbActivityLogFetch(ex, { immediate: true });
    }
    void this._fetchDeliveryValidation(ex);
    this._queueMindmapSyncFromDb();
  }

  private _queueMindmapSyncFromDb(): void {
    queueMicrotask(() => {
      const mindmap = this.renderRoot?.querySelector(
        "ead-pfm-mindmap-mermaid.current-context-mindmap",
      ) as { syncFromDb?: () => Promise<void> } | null;
      void mindmap?.syncFromDb?.();
    });
  }

  private _onPfmMindmapOptimizeRequested(): void {
    void executionsStore.refresh(true).then(() => {
      queueMicrotask(() => {
        const ex = executionsStore.selectedExecution;
        const mindmap = this.renderRoot?.querySelector(
          "ead-pfm-mindmap-mermaid.current-context-mindmap",
        ) as { syncFromDb?: () => Promise<void> } | null;
        void mindmap?.syncFromDb?.();
        if (ex?.id) {
          this._lastSeenPfmRevisionRunId = ex.id;
          this._lastSeenPfmRevisionForRun = Math.max(0, Number(ex.pfmRevision ?? 0));
        }
        this.requestUpdate();
      });
    });
  }

  private _fingerprintOf(value: string): string {
    const input = String(value || "");
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return `ss-${(hash >>> 0).toString(16)}`;
  }

  private _toEpochMs(value: number): number {
    const ts = Number(value || 0);
    if (!Number.isFinite(ts) || ts <= 0) return Date.now();
    // Backend sometimes sends epoch seconds; UI expects epoch milliseconds.
    return ts < 1_000_000_000_000 ? ts * 1000 : ts;
  }

  private _extractRunScreenshots(ex: ProjectExecute): RunScreenshot[] {
    const executionId = String(ex.id || "").trim();
    const byFingerprint = new Map<string, RunScreenshot>();
    const add = (candidate: Omit<RunScreenshot, "fingerprint">): void => {
      const url = resolveExecutionReportUrl(String(candidate.url || "").trim(), executionId);
      const thumb = resolveExecutionReportUrl(
        String(candidate.thumbnailUrl || candidate.url || "").trim(),
        executionId,
      );
      if (!url && !thumb) return;
      const caption = String(candidate.caption || "").trim();
      const step = String(candidate.relatedStepId || "").trim();
      const ts = Number(candidate.timestamp || 0);
      const fp = this._fingerprintOf([
        url || thumb,
        thumb,
        step,
        caption.slice(0, 120),
      ].join("|"));
      const next: RunScreenshot = {
        fingerprint: fp,
        url: url || thumb,
        thumbnailUrl: thumb || url,
        caption: caption || "Run screenshot",
        timestamp: Number.isFinite(ts) && ts > 0 ? this._toEpochMs(ts) : Date.now(),
        relatedStepId: step || "progress-log",
      };
      const existing = byFingerprint.get(fp);
      if (!existing || next.timestamp > existing.timestamp) byFingerprint.set(fp, next);
    };

    for (const entry of ex.progressLog ?? []) {
      const imageUrl = String(entry.imageUrl || "").trim();
      const thumbUrl = String(entry.thumbnailUrl || "").trim();
      if (!imageUrl && !thumbUrl) continue;
      add({
        url: imageUrl || thumbUrl,
        thumbnailUrl: thumbUrl || imageUrl,
        caption: String(entry.text || entry.toolName || "Run screenshot"),
        timestamp: this._toEpochMs(Number(entry.ts || 0)),
        relatedStepId: String(entry.toolName || entry.kind || "progress-log"),
      });
    }

    for (const step of ex.steps || []) {
      for (const artifact of step.artifacts || []) {
        if ((artifact.type || "") !== "screenshot") continue;
        const path = String(artifact.path || "").trim();
        const thumb = String(artifact.thumbnailPath || path).trim();
        if (!path && !thumb) continue;
        add({
          url: path || thumb,
          thumbnailUrl: thumb || path,
          caption: String(artifact.description || step.title || "Step screenshot"),
          timestamp: Date.parse(String(artifact.capturedAt || "")) || Date.now(),
          relatedStepId: String(step.stepId || "step"),
        });
      }
    }

    return [...byFingerprint.values()].sort((a, b) => b.timestamp - a.timestamp);
  }

  private _extractRunActivities(ex: ProjectExecute): RunActivity[] {
    const executionId = String(ex.id || "").trim();
    const progress = ex.progressLog ?? [];
    const screenshotPool = this._extractRunScreenshots(ex);
    const activities: RunActivity[] = [];
    let lastLogicalTs = 0;

    for (const entry of progress) {
      if (String(entry.kind || "") !== "tool_use") continue;
      if (String(entry.toolName || "") !== "report_running_step") continue;
      const rawTs = this._toEpochMs(Number(entry.ts || 0));
      const ts = rawTs <= lastLogicalTs ? lastLogicalTs + 1000 : rawTs;
      lastLogicalTs = ts;
      const ti = (entry.toolInput || {}) as Record<string, unknown>;
      const rawTitle = String(ti.title || "").trim();
      const finding = String(ti.finding || "").trim();
      const reasoning = String(ti.reasoning || "").trim();
      const nextDirection = String(ti.next_direction || ti.nextDirection || "").trim();
      const prose = formatConversationalActivityProse(rawTitle || this._activityText(entry), {
        finding,
        reasoning,
        nextDirection,
      });
      const text =
        prose.paragraphs.join("\n\n").trim() ||
        stripOperatorActivityPhasePrefix(
          formatOperatorActivityLine(rawTitle || this._activityText(entry), {
            finding,
            reasoning,
            nextDirection,
          }).display,
        ).trim() ||
        this._activityText(entry).trim();
      const operatorPhase = prose.phase;
      const imageUrl = String(entry.imageUrl || "").trim();
      const thumbUrl = String(entry.thumbnailUrl || "").trim();
      const tiThumbRaw = ti.thumbnail_urls ?? ti.thumbnailUrls;
      const tiThumbs = Array.isArray(tiThumbRaw)
        ? tiThumbRaw
            .map((v) => String(v || "").trim())
            .filter((v) => Boolean(v))
        : [];
      const screenshotUrls = [
        ...new Set(
          [imageUrl, thumbUrl, ...tiThumbs]
            .map((v) => resolveExecutionReportUrl(String(v || "").trim(), executionId))
            .filter((v) => Boolean(v)),
        ),
      ];
      let hasImage = screenshotUrls.length > 0;
      const stepId = String(entry.toolName || entry.kind || "activity");
      const kind = String(entry.kind || "assistant");
      let screenshots: RunScreenshot[] = hasImage
        ? screenshotUrls.map((url, idx) => {
            const resolvedThumb = resolveExecutionReportUrl(
              idx === 0 && thumbUrl ? thumbUrl : url,
              executionId,
            );
            return {
              fingerprint: this._fingerprintOf([url, idx, ts].join("|")),
              url,
              thumbnailUrl: resolvedThumb || url,
              caption: text || "Screenshot captured",
              timestamp: ts,
              relatedStepId: stepId,
            };
          })
        : [];

      if (!screenshots.length && screenshotPool.length) {
        const nearby = screenshotPool.filter((shot) => Math.abs(shot.timestamp - ts) <= 8000);
        if (nearby.length) {
          screenshots = nearby.map((shot) => ({
            ...shot,
            caption: text || shot.caption,
            timestamp: ts,
            relatedStepId: stepId,
          }));
          hasImage = true;
        }
      }

      if (text) {
        activities.push({
          fingerprint: this._fingerprintOf(
            [ts, stepId, rawTitle.slice(0, 160), screenshotUrls.join("|")].join("|"),
          ),
          timestamp: ts,
          kind,
          text,
          operatorPhase,
          relatedStepId: stepId,
          screenshots,
        });
      }
    }

    return activities.sort((a, b) => b.timestamp - a.timestamp);
  }

  private _scheduleDbActivityLogFetch(
    ex: ProjectExecute,
    opts?: { immediate?: boolean },
  ): void {
    const runId = String(ex.id || "").trim();
    if (!runId) return;
    if (!this._shouldPollActivityLog()) return;
    const minIntervalMs = isLiveProjectExecute(ex) ? 15_000 : 30_000;
    const waitMs = opts?.immediate
      ? 0
      : Math.max(0, minIntervalMs - (Date.now() - this._activityLogLastFetchAt));
    if (this._activityLogFetchTimer != null) {
      window.clearTimeout(this._activityLogFetchTimer);
      this._activityLogFetchTimer = null;
    }
    this._activityLogFetchTimer = window.setTimeout(() => {
      this._activityLogFetchTimer = null;
      void this._fetchDbActivityLog(ex);
    }, waitMs);
  }

  private async _fetchDbActivityLog(ex: ProjectExecute): Promise<void> {
    const runId = String(ex.id || "").trim();
    if (!runId || this._activityLogInFlight || !canPollGateway()) return;
    this._activityLogInFlight = true;
    this._activityLogLastFetchAt = Date.now();
    try {
      const incremental =
        this._dbActivitiesRunId === runId && this._dbActivitiesMaxSeq > 0;
      const resp = await runGatewayPoll(() =>
        hermesClient.request<{
          entries?: Array<Record<string, unknown>>;
          totalCount?: number;
        }>("executions.activityLog", {
          executionId: runId,
          limit: 50,
          sinceSeq: incremental ? this._dbActivitiesMaxSeq : undefined,
        }),
      );
      if (runId !== (executionsStore.selectedExecution?.id || "")) return;
      const rawEntries = resp?.entries || [];
      const mapped = this._mapDbActivityEntries(rawEntries, runId);
      if (incremental && mapped.length) {
        const seen = new Set(this._dbActivities.map((a) => a.fingerprint));
        const merged = [...mapped.filter((a) => !seen.has(a.fingerprint)), ...this._dbActivities];
        this._dbActivities = merged.sort((a, b) => b.timestamp - a.timestamp);
      } else if (!incremental) {
        this._dbActivities = mapped;
      }
      for (const row of rawEntries) {
        const seq = Number(row.seq ?? 0);
        if (Number.isFinite(seq) && seq > this._dbActivitiesMaxSeq) {
          this._dbActivitiesMaxSeq = seq;
        }
      }
      this._dbActivitiesRunId = runId;
      this._dbActivitiesLoaded = true;
      mergeRunActivitiesPersisted(runId, this._dbActivities);
      this.requestUpdate();
    } finally {
      this._activityLogInFlight = false;
    }
  }

  private _mapDbActivityEntries(
    entries: Array<Record<string, unknown>>,
    executionId: string,
  ): RunActivity[] {
    const chronological = [...entries].reverse();
    const groups: Array<Array<Record<string, unknown>>> = [];
    let current: Array<Record<string, unknown>> = [];
    let lastStepKey = "";
    for (const row of chronological) {
      const stepKey = String(row.stepId || row.id || "").trim();
      if (stepKey && stepKey === lastStepKey && current.length) {
        current.push(row);
      } else {
        if (current.length) groups.push(current);
        current = [row];
        lastStepKey = stepKey;
      }
    }
    if (current.length) groups.push(current);

    const activities: RunActivity[] = [];
    for (const group of groups) {
      const head = group[0] || {};
      const ts = Number(head.createdAtMs || head.created_at_ms || 0) || Date.now();
      const rawTitle = String(head.title || "").trim();
      const finding = String(head.finding || "").trim();
      const reasoning = String(head.reasoning || "").trim();
      const nextDirection = String(head.nextDirection || head.next_direction || "").trim();
      const prose = formatConversationalActivityProse(rawTitle, {
        finding,
        reasoning,
        nextDirection,
      });
      const text =
        prose.paragraphs.join("\n\n").trim() ||
        stripOperatorActivityPhasePrefix(
          formatOperatorActivityLine(rawTitle, { finding, reasoning, nextDirection }).display,
        ).trim() ||
        rawTitle;
      const operatorPhase = (prose.phase ||
        String(head.operatorPhase || head.operator_phase || "")) as OperatorActivityPhase | "";
      const stepId = String(head.stepId || head.step_id || "activity");
      const screenshotUrls = [
        ...new Set(
          group
            .map((row) =>
              resolveExecutionReportUrl(
                String(row.thumbnailUrl || row.thumbnail_url || "").trim(),
                executionId,
              ),
            )
            .filter((url) => Boolean(url)),
        ),
      ];
      const screenshots: RunScreenshot[] = screenshotUrls.map((url, idx) => ({
        fingerprint: this._fingerprintOf([url, stepId, idx, ts].join("|")),
        url,
        thumbnailUrl: url,
        caption: text || "Screenshot captured",
        timestamp: ts,
        relatedStepId: stepId,
      }));
      if (!text && !screenshots.length) continue;
      activities.push({
        fingerprint: this._fingerprintOf(
          [ts, stepId, rawTitle.slice(0, 160), screenshotUrls.join("|")].join("|"),
        ),
        timestamp: ts,
        kind: String(head.kind || "tool_use"),
        text,
        operatorPhase,
        relatedStepId: stepId,
        screenshots,
      });
    }
    return activities.sort((a, b) => b.timestamp - a.timestamp);
  }

  /** DB activity log when present; else progress_log + local cache. */
  private _runActivitiesForDisplay(ex: ProjectExecute): RunActivity[] {
    if (!ex.id) return [];
    const useDb =
      this._dbActivitiesLoaded
      && this._dbActivitiesRunId === ex.id
      && this._dbActivities.length > 0;
    const base = useDb ? this._dbActivities : this._extractRunActivities(ex);
    const merged = mergeRunActivitiesPersisted(ex.id, base);
    return merged.map((activity) => ({
      ...activity,
      operatorPhase: (activity.operatorPhase || "") as RunActivity["operatorPhase"],
    }));
  }

  private _hasMeaningfulActivityExplanation(text: string): boolean {
    const normalized = String(text || "").trim();
    if (!normalized) return false;
    const withoutPhase = normalized.replace(/^\[[^\]]+\]\s*/, "").trim();
    const compact = withoutPhase.toLowerCase().replace(/\s+/g, " ");
    if (
      [
        "screenshot",
        "screenshot captured",
        "recovered screenshot",
        "run screenshot",
        "step screenshot",
      ].includes(compact)
    ) {
      return false;
    }
    return withoutPhase.length >= 18;
  }

  private _activityDisplayLine(activity: RunActivity): string {
    const text = stripOperatorActivityPhasePrefix(String(activity.text || "").trim());
    if (text) return text;
    const label = activity.relatedStepId || activity.kind || "activity";
    return `[${label}]`;
  }

  private _renderActivityText(activity: RunActivity): ReturnType<typeof html> {
    const line = this._activityDisplayLine(activity);
    const { paragraphs } = formatConversationalActivityProse(line);
    if (!paragraphs.length) {
      return html`<span class="activity-title-body">${line}</span>`;
    }
    return html`
      <div class="activity-prose">
        ${paragraphs.map((paragraph) => html`<p class="activity-title-body">${paragraph}</p>`)}
      </div>
    `;
  }

  private _syncScreenshotSelection(ex?: ProjectExecute, _runChanged = false): void {
    if (!ex?.id) {
      this._activeScreenshotFingerprint = "";
      return;
    }
    const activities = this._runActivitiesForDisplay(ex);
    const screenshotActivities = activities.filter((a) => a.screenshots.length);
    if (!screenshotActivities.length) {
      this._activeScreenshotFingerprint = "";
      this._screenshotSelectionByExecution.delete(ex.id);
      return;
    }
    const preferred = screenshotActivities[0].fingerprint;
    const remembered = this._screenshotSelectionByExecution.get(ex.id) || "";
    const rememberedExists = screenshotActivities.some((a) => a.fingerprint === remembered);
    const current = !_runChanged && rememberedExists ? remembered : preferred;
    const latest = preferred;
    const isActive = isLiveProjectExecute(ex);
    // During live runs, auto-focus latest activity when it changes.
    const next = isActive && latest !== current ? latest : current;
    this._screenshotSelectionByExecution.set(ex.id, next);
    this._activeScreenshotFingerprint = next;
  }

  private _activeContextPath(): DrillAncestor[] {
    const safeDepth = Math.max(0, Math.min(this._activeContextDepth, this._contextPath.length));
    return this._contextPath.slice(0, safeDepth);
  }

  private async _stopExecution(run: ProjectExecute, mode: "finish" | "cancel"): Promise<void> {
    this._confirmRunError = "";
    this._stopRunError = "";
    this._confirmRunAction = null;
    this._stopMenuOpenFor = null;
    this._runOverflowMenuFor = null;
    this._stoppingRunId = run.id;
    try {
      await executionsStore.cancelExecution(run.id, mode);
    } catch (err) {
      this._stopRunError = err instanceof Error ? err.message : String(err);
    } finally {
      if (this._stoppingRunId === run.id) {
        this._stoppingRunId = null;
      }
    }
  }

  private async _extendRunBudget(run: ProjectExecute, minutes: number): Promise<void> {
    this._stopRunError = "";
    this._stopMenuOpenFor = null;
    this._runOverflowMenuFor = null;
    this._extendingRunId = run.id;
    try {
      await executionsStore.extendExecutionBudget(run.id, minutes);
      notifyOperator({
        title: "Run extended",
        message: `Added ${minutes} minutes to this run budget.`,
        type: "success",
      });
    } catch (err) {
      this._stopRunError = err instanceof Error ? err.message : String(err);
    } finally {
      if (this._extendingRunId === run.id) {
        this._extendingRunId = null;
      }
    }
  }

  private _defaultAnswerLanguage(): AiAnswerLanguage {
    return defaultAnswerLanguage(this.locale);
  }

  private _answerLanguageValue(ex: ProjectExecute): AiAnswerLanguage {
    return normalizeAnswerLanguage(
      String(ex.aiAnswerLanguage || ""),
      this._defaultAnswerLanguage(),
    );
  }

  private _answerLanguageLabel(code: AiAnswerLanguage): string {
    return answerLanguageLabel(code, (key) => this._t(key));
  }

  private async _setExecutionAnswerLanguage(ex: ProjectExecute, code: AiAnswerLanguage): Promise<void> {
    const current = this._answerLanguageValue(ex);
    if (current === code) return;
    this._stopRunError = "";
    this._answerLanguageUpdatingRunId = ex.id;
    try {
      await executionsStore.setExecutionAnswerLanguage(ex.id, code);
      const sessionKey = resolveRunSessionKey(ex);
      if (sessionKey.trim()) {
        await hermesClient.request("chat.inject", {
          sessionKey,
          role: "system",
          label: "[AI-ANSWER-LANGUAGE:v1]",
          content: [
            "[AI-ANSWER-LANGUAGE:v1]",
            `Use ${this._answerLanguageLabel(code)} from now on for operator-facing chat replies and report_running_step activity fields (title, finding, next_direction, reasoning).`,
            "Apply immediately unless the operator asks to switch language again.",
          ].join("\n\n"),
        });
      }
      notifyOperator({
        title: this._t("runs.aiAnswerLanguageUpdatedTitle"),
        message: this._t("runs.aiAnswerLanguageUpdatedMessage", { language: this._answerLanguageLabel(code) }),
        type: "success",
      });
    } catch (err) {
      this._stopRunError = err instanceof Error ? err.message : String(err);
    } finally {
      if (this._answerLanguageUpdatingRunId === ex.id) {
        this._answerLanguageUpdatingRunId = null;
      }
    }
  }

  private async _deleteExecution(ex: ProjectExecute): Promise<void> {
    this._confirmRunError = "";
    try {
      await executionsStore.deleteExecution(ex.id);
      clearPersistedRunActivities(ex.id);
      this._confirmRunAction = null;
    } catch (err) {
      this._confirmRunAction = { action: "delete", run: ex };
      this._confirmRunError = err instanceof Error ? err.message : String(err);
    }
  }

  private async _invalidateExecution(ex: ProjectExecute): Promise<void> {
    this._confirmRunError = "";
    try {
      await executionsStore.invalidateExecution(
        ex.id,
        "Not valid for data reporting and training.",
      );
      this._confirmRunAction = null;
    } catch (err) {
      this._confirmRunAction = { action: "invalidate", run: ex };
      this._confirmRunError = err instanceof Error ? err.message : String(err);
    }
  }

  private _isChatDeliveryActive(ex: ProjectExecute): boolean {
    if (typeof ex.chatDeliveryActive === "boolean") return ex.chatDeliveryActive;
    return String(ex.reportingActivityStatus || "active").trim().toLowerCase() === "active";
  }

  /** Finished runs may still use operator chat; sending a message re-activates that run. */
  private _isTerminalRunEligibleForChat(ex: ProjectExecute): boolean {
    if (isLiveProjectExecute(ex)) return false;
    const status = String(ex.status || "").trim().toLowerCase();
    return status === "completed" || status === "failed" || status === "cancelled" || status === "error";
  }

  private _openRunConfirm(action: RunConfirmAction, run: ProjectExecute): void {
    this._confirmRunError = "";
    this._runOverflowMenuFor = null;
    this._confirmRunAction = { action, run };
  }

  private _runsForTemplate(templateId?: string): ProjectExecute[] {
    if (!templateId) return [];
    return executionsStore.executions.filter((run) => run.linkedTemplateId === templateId);
  }

  private _validTrainingRunsForTemplate(templateId?: string): ProjectExecute[] {
    if (!templateId) return [];
    return this._runsForTemplate(templateId)
      .filter(isExecutionEligibleForPfmInheritance)
      .sort(sortExecutionsByRunNumberDesc);
  }

  private async _handleMindmapNodeSelected(
    ex: ProjectExecute,
    event: CustomEvent<{
      nodeKey?: string;
      title?: string;
      source?: string;
      nodePath?: string[];
      drillDown?: boolean;
    }>,
  ): Promise<void> {
    // Selection/drill only — mindmap fetches via pfm.mindmap inside the component. No requestSnapshot.
    const rawKey = String(event.detail?.nodeKey || "").trim();
    if (event.detail?.source !== "committed-tree") return;
    const knownKeys = (ex.results || [])
      .map((node) => String((node.nodeKey || node.nodeId || "") as string).trim())
      .filter(Boolean);
    const nodeKey = resolveCommittedNodeKeyInTree(rawKey, knownKeys);
    const title = stripPfmDisplayTitle(String(event.detail?.title || nodeKey || "PFM Node"));
    if (!nodeKey) return;
    if (event.detail?.drillDown !== true) {
      const parents = this._activeContextPath();
      const contextCacheKey = buildMindmapCacheKey(resolveRunSessionKey(ex), parents);
      this._recordVisitedMindmapNode(ex, nodeKey, title, parents);
      return;
    }
    const pathKeys = Array.isArray(event.detail?.nodePath)
      ? event.detail.nodePath.map((k) => String(k || "").trim()).filter(Boolean)
      : [];
    let nextPath: DrillAncestor[];
    if (pathKeys.length) {
      const parentByKey = this._committedParentByKey(ex);
      const titleByKey = new Map<string, string>();
      const knownKeys: string[] = [];
      for (const node of ex.results || []) {
        const key = String((node.nodeKey || node.nodeId || "") as string).trim();
        const ttl = String(node.title || key).trim();
        if (key) {
          knownKeys.push(key);
          if (ttl) titleByKey.set(key, ttl);
        }
      }
      nextPath = pathKeys.map((nk) => {
        const canonical = resolveCommittedNodeKeyInTree(nk, knownKeys);
        const pathTitle = stripPfmDisplayTitle(
          titleByKey.get(canonical)
            || (title && canonical === resolveCommittedNodeKeyInTree(nodeKey, knownKeys)
              ? title
              : "")
            || canonical.split("/").pop()
            || canonical,
        );
        return { nodeKey: canonical, title: pathTitle };
      });
      const hubKeys = collectCenterHubKeysFromParentMap(parentByKey, titleByKey);
      nextPath = collapseConsecutiveDrillPath(
        filterInternalHubFromDrillPath(nextPath, hubKeys),
      );
    } else {
      nextPath = this._nodePathForKey(ex, nodeKey, title, { canonicalKeys: true });
    }
    nextPath = collapseConsecutiveDrillPath(nextPath);
    const existingPath = collapseConsecutiveDrillPath([...this._contextPath]);
    const isAncestorOfExisting =
      nextPath.length > 0
      && existingPath.length > nextPath.length
      && nextPath.every((item, idx) => item.nodeKey === existingPath[idx]?.nodeKey);
    const resolvedPath = collapseConsecutiveDrillPath(
      isAncestorOfExisting ? existingPath : nextPath,
    );
    const resolvedDepth = isAncestorOfExisting ? nextPath.length : resolvedPath.length;
    const activeNode = resolvedPath[Math.max(0, resolvedDepth - 1)];
    const parents = resolvedPath.slice(0, Math.max(0, resolvedDepth - 1));
    const contextCacheKey = buildMindmapCacheKey(resolveRunSessionKey(ex), parents);
    this._recordVisitedMindmapNode(
      ex,
      activeNode?.nodeKey || nodeKey,
      activeNode?.title || title,
      parents,
      { contextPath: resolvedPath, activeDepth: resolvedDepth },
    );
    await this.updateComplete;
  }

  private _recordVisitedMindmapNode(
    ex: ProjectExecute,
    nodeKey: string,
    title: string,
    parents: DrillAncestor[],
    opts?: { contextPath?: DrillAncestor[]; activeDepth?: number },
  ): void {
    const contextCacheKey = buildMindmapCacheKey(resolveRunSessionKey(ex), parents);
    saveVisitedPfmNode(ex.id, contextCacheKey, nodeKey, title);
    this._pfmVisitEpoch += 1;
    this._selectedNode = { nodeKey, title, parents };
    if (Array.isArray(opts?.contextPath)) {
      this._contextPath = opts.contextPath;
      this._activeContextDepth = Math.max(0, Number(opts.activeDepth ?? opts.contextPath.length));
    }
    this.requestUpdate();
  }

  private async _navigateToPathDepth(depth: number): Promise<void> {
    if (depth <= 0) {
      this._activeContextDepth = 0;
      // Keep _contextPath (e.g. User Settings) so one breadcrumb shows Top › … with Top active.
      await this.updateComplete;
      await this._syncCommittedMindmapFocus("");
      return;
    }
    const nextDepth = Math.max(0, Math.min(depth, this._contextPath.length));
    this._activeContextDepth = nextDepth;
    const target = this._contextPath[nextDepth - 1];
    if (!target?.nodeKey) {
      this._selectedNode = null;
      await this.updateComplete;
      await this._syncCommittedMindmapFocus("");
      return;
    }
    this._selectedNode = {
      nodeKey: target.nodeKey,
      title: target.title,
      parents: this._contextPath.slice(0, nextDepth - 1),
    };
    await this.updateComplete;
    await this._syncCommittedMindmapFocus(target.nodeKey);
  }

  private _scheduleExploreMindmapLoad(): void {
    if (this._leftTab !== "explore") return;
    const ex = executionsStore.selectedExecution;
    if (!ex?.id || this._shouldDeferPfmMindmap(ex)) return;
    requestAnimationFrame(() => {
      void this.updateComplete.then(() => {
        const mindmap = this.shadowRoot?.querySelector(".current-context-mindmap") as
          | (HTMLElement & { navigateCommittedTop?: () => void })
          | null;
        mindmap?.navigateCommittedTop?.();
      });
    });
  }

  private async _syncCommittedMindmapFocus(nodeKey: string): Promise<void> {
    await this.updateComplete;
    const mindmap = this.shadowRoot?.querySelector(".current-context-mindmap") as
      | (HTMLElement & { navigateCommittedFocus?: (key: string) => void })
      | null;
    const focusKey = String(nodeKey || "").trim();
    if (focusKey) {
      mindmap?.navigateCommittedFocus?.(focusKey);
    } else {
      (mindmap as HTMLElement & { navigateCommittedTop?: () => void })?.navigateCommittedTop?.();
    }
  }

  private _breadcrumbLabel(value: string): string {
    const cleaned = stripPfmDisplayTitle(String(value || "").trim());
    // Hide meaningless ordinal prefixes like "8-report", "12. Report", "3) Report".
    return cleaned
      .replace(/^\s*\d+\s*[-.)]\s*/u, "")
      .trim();
  }

  private _committedParentByKey(
    ex: ProjectExecute,
  ): Map<string, string | null | undefined> {
    const parentByKey = new Map<string, string | null | undefined>();
    for (const node of ex.results || []) {
      const key = String((node.nodeKey || node.nodeId || "") as string).trim();
      if (!key) continue;
      parentByKey.set(key, node.parentNodeKey ?? null);
    }
    return parentByKey;
  }

  private _nodePathForKey(
    ex: ProjectExecute,
    nodeKey: string,
    fallbackTitle: string,
    options?: { canonicalKeys?: boolean },
  ): DrillAncestor[] {
    const rawKey = String(nodeKey || "").trim();
    if (!rawKey) return [];
    const titleByKey = new Map<string, string>();
    const parentByKey = this._committedParentByKey(ex);
    const knownKeys: string[] = [];
    for (const node of ex.results || []) {
      const key = String((node.nodeKey || node.nodeId || "") as string).trim();
      const ttl = String(node.title || key).trim();
      if (key) {
        knownKeys.push(key);
        if (ttl) titleByKey.set(key, ttl);
      }
    }
    const cleanKey = options?.canonicalKeys
      ? resolveCommittedNodeKeyInTree(rawKey, knownKeys)
      : collapseDuplicateHubInNodeKey(rawKey);
    if (!cleanKey) return [];
    const hubKeys = collectCenterHubKeysFromParentMap(parentByKey, titleByKey);
    const path = buildDrillPathFromParents(
      cleanKey,
      titleByKey,
      parentByKey,
      fallbackTitle,
    );
    return filterInternalHubFromDrillPath(path, hubKeys);
  }

  private _shortCenterLabel(value: string): string {
    const clean = String(value || "").replace(/\s+/g, " ").trim();
    if (!clean) return "PFM";
    if (clean.length <= 16) return clean;
    return `${clean.slice(0, 16).trim()}…`;
  }

  private _inheritedExecutionIdFromProgressLog(ex: ProjectExecute): string {
    for (const entry of [...(ex.progressLog || [])].reverse()) {
      const blob = String(entry.text || "");
      const m = blob.match(
        /Inherited prior PFM context from run\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
      );
      if (m?.[1]) return m[1].trim();
    }
    return "";
  }

  /** V/Rev from API fields or instant client lookup on template runs (run_number + pfm_revision). */
  private _resolvePfmLineageForUi(ex: ProjectExecute) {
    const templateId = (ex.linkedTemplateId || "").trim();
    const peers = templateId ? this._runsForTemplate(templateId) : [];
    return resolveClientPfmLineage(ex, peers, {
      baselineExecutionId: this._resolvedPfmBaselineExecutionId(ex),
      inheritedExecutionId:
        String(ex.inheritedFromExecutionId || "").trim()
        || this._inheritedExecutionIdFromProgressLog(ex),
      treeReadExecutionId: this._resolvedPfmTreeReadExecutionId(ex),
    });
  }

  private _formatRunNumberOverview(ex: ProjectExecute): string {
    const lineage = this._resolvePfmLineageForUi(ex);
    const runNo = Math.max(
      0,
      Number(ex.runNumber || 0),
      Number(ex.pfmGenerationVersion || 0),
      lineage.currentRunNumber,
    );
    if (runNo <= 0) return "—";
    // Before this run commits its own PFM (e.g. Initializing), only show V — no Rev yet.
    if (ex.pfmHasCommittedSnapshot !== true) {
      return `V ${runNo}`;
    }
    const rev = Math.max(
      0,
      Number(ex.pfmRevision ?? 0),
      Number(ex.currentPfmRevision ?? 0),
      lineage.currentPfmRevision,
    );
    return rev > 0 ? `V ${runNo} (Rev ${rev})` : `V ${runNo}`;
  }

  /** True when this run must use its own mindmap — never baseline/previous for display. */
  private _runHasOwnMindmap(ex: ProjectExecute): boolean {
    const ownId = String(ex.id || "").trim();
    if (!ownId) return false;
    const mode = String(ex.pfmMapDisplayMode || "").trim().toLowerCase();
    const hasDelivered = ex.hasDeliveredPfmMapReport === true;
    if (mode === "previous" && !hasDelivered) return false;
    if (hasDelivered) return true;
    const ownRev = Math.max(
      0,
      Number(ex.pfmRevision ?? 0),
      Number(ex.currentPfmRevision ?? 0),
      Number(ex.pfmStepVersion ?? 0),
    );
    const serverRead = String(ex.pfmTreeReadExecutionId || "").trim();
    if (hasDelivered && ownRev > 0 && serverRead === ownId) return true;
    return hasDelivered && ownRev > 0 && mode === "current";
  }

  /** Mindmap + overview: treat promoted pfm_revision / committed snapshot as “has DB map”. */
  /** Pre-delivery: read baseline pfm_nodes (Most Recent). Post-delivery: read this run after import_pfm_delivery. */
  private _mindmapLineageForRun(ex: ProjectExecute) {
    const lineage = this._resolvePfmLineageForUi(ex);
    const ownRev = Math.max(
      0,
      Number(ex.pfmRevision ?? 0),
      Number(ex.currentPfmRevision ?? 0),
      Number(ex.pfmStepVersion ?? 0),
      lineage.currentPfmRevision,
    );
    const ownRun = Math.max(
      0,
      Number(ex.runNumber || 0),
      Number(ex.pfmGenerationVersion || 0),
      lineage.currentRunNumber,
    );
    const ownId = String(ex.id || "").trim();

    // Invariant: own saved mindmap always wins — never show baseline tree for this run.
    if (this._runHasOwnMindmap(ex)) {
      return {
        lineage: {
          ...lineage,
          hasCurrentPfmMap: true,
          currentRunNumber: ownRun,
          currentPfmRevision: ownRev,
          previousPfmRunNumber: 0,
          previousPfmRevision: 0,
        },
        displayMode: "current" as const,
        treeReadExecutionId: ownId,
        hasCommittedSnapshot: true,
      };
    }

    const serverMode = String(ex.pfmMapDisplayMode || "").trim().toLowerCase();
    const hasDelivered = ex.hasDeliveredPfmMapReport === true;
    const hasOwnDbMap =
      hasDelivered
      || (hasDelivered && serverMode === "current" && ownRev > 0);
    const treeReadId = this._resolvedPfmTreeReadExecutionId(ex);
    const readingBaseline =
      serverMode === "previous"
      || Boolean(treeReadId && treeReadId !== ownId);
    if (!hasOwnDbMap || readingBaseline) {
      const baselineId = this._resolvedPfmBaselineExecutionId(ex);
      const resolvedTreeRead = treeReadId || baselineId;
      const hasInherited =
        Boolean(resolvedTreeRead && resolvedTreeRead !== ownId)
        || lineage.previousPfmRunNumber > 0
        || Number(ex.pfmSourceRunNumber || 0) > 0;
      return {
        lineage: {
          ...lineage,
          previousPfmRunNumber: Math.max(
            lineage.previousPfmRunNumber,
            Number(ex.pfmSourceRunNumber || 0),
            Number(ex.pfmBaselineTreeVersion || 0),
          ),
        },
        displayMode: serverMode === "previous" || hasInherited ? "previous" : (ex.pfmMapDisplayMode || ""),
        treeReadExecutionId: resolvedTreeRead,
        hasCommittedSnapshot: hasOwnDbMap,
      };
    }
    return {
      lineage: {
        ...lineage,
        hasCurrentPfmMap: true,
        currentRunNumber: ownRun,
        currentPfmRevision: ownRev,
        previousPfmRunNumber: 0,
        previousPfmRevision: 0,
      },
      displayMode: "current" as const,
      treeReadExecutionId: String(ex.id || "").trim(),
      hasCommittedSnapshot: true,
    };
  }

  private _pfmLineageHasVersion(ex: ProjectExecute): boolean {
    if (ex.pfmHasCommittedSnapshot === true) {
      return Math.max(0, Number(ex.runNumber || ex.pfmGenerationVersion || 0)) > 0;
    }
    return (
      Math.max(
        0,
        Number(ex.previousPfmRunNumber || 0),
        Number(ex.pfmSourceRunNumber || 0),
        Number(ex.pfmBaselineTreeVersion || 0),
      ) > 0
    );
  }

  private _pfmMindmapSectionTitle(ex: ProjectExecute): string {
    const templateId = (ex.linkedTemplateId || "").trim();
    const peers = templateId ? this._runsForTemplate(templateId) : [];
    return buildPfmMindmapHeaderLabel(ex, peers, {
      baselineExecutionId: this._resolvedPfmBaselineExecutionId(ex),
      inheritedExecutionId:
        String(ex.inheritedFromExecutionId || "").trim()
        || this._inheritedExecutionIdFromProgressLog(ex),
      treeReadExecutionId: this._resolvedPfmTreeReadExecutionId(ex),
    });
  }

  private _currentMindmapRootLabel(ex: ProjectExecute): string {
    const activePath = this._activeContextPath();
    if (activePath.length > 0) {
      return this._shortCenterLabel(activePath[activePath.length - 1]?.title || "PFM");
    }
    return this._shortCenterLabel(ex.name || "PFM");
  }

  private _extractReportSummary(markdown: string): string {
    const lines = String(markdown || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    for (const line of lines) {
      if (/^#{1,6}\s/.test(line)) continue;
      if (/^[-*]\s/.test(line)) continue;
      if (/^\d+\.\s/.test(line)) continue;
      if (/^\[.*\]$/.test(line)) continue;
      const clean = line.replace(/\*\*/g, "").replace(/`/g, "").trim();
      if (!clean) continue;
      if (clean.length <= 90) return clean;
      return `${clean.slice(0, 90).trim()}…`;
    }
    return "";
  }

  private _currentContextLeafDescription(ex: ProjectExecute): string {
    const activePath = this._activeContextPath();
    if (!activePath.length) return "";
    const current = activePath[activePath.length - 1];
    const parents = activePath.slice(0, -1);
    const scopeKey = buildScopeKey(parents, current.nodeKey);
    const report = loadPfmNodeReport(ex.id, scopeKey);
    if (!report?.content) return "";
    const summary = this._extractReportSummary(report.content);
    return summary ? `Description: ${summary}` : "";
  }

  private _currentContextCacheKey(ex: ProjectExecute): string {
    return buildMindmapCacheKey(resolveRunSessionKey(ex), this._activeContextPath());
  }

  private _visitedNodeKeysForCurrentContext(ex: ProjectExecute): string[] {
    void this._pfmVisitEpoch;
    const contextCacheKey = this._currentContextCacheKey(ex);
    return loadVisitedPfmNodes(ex.id, contextCacheKey);
  }

  private _activePathNodeKeysForCurrentContext(ex: ProjectExecute): string[] {
    const contextCacheKey = this._currentContextCacheKey(ex);
    const activeNodeKey = loadActivePfmNode(ex.id, contextCacheKey);
    return activeNodeKey ? [activeNodeKey] : [];
  }

  /** Operator-facing label from gateway projectRunStatus (0–7) only. */
  private _displayStatusLabel(ex: ProjectExecute): string {
    return projectRunStatusLabelForExecution(ex);
  }

  private _runPhaseCode(ex: ProjectExecute): number {
    return readProjectRunStatus(ex);
  }

  /** Human-viewable activity entries: screenshot + meaningful Found/Next text. */
  private _viewerActivityLogCount(ex: ProjectExecute): number {
    return this._runActivitiesForDisplay(ex).filter((activity) => {
      if (!activity.screenshots.length) return false;
      return this._hasMeaningfulActivityExplanation(this._activityDisplayLine(activity));
    }).length;
  }

  private _runningLogCount(ex: ProjectExecute): number {
    return this._viewerActivityLogCount(ex);
  }

  private _isAbandonedByRunPhaseFields(ex: ProjectExecute): boolean {
    const hasStart = Number(ex.runningPhaseStartedAtMs || 0) > 0;
    const hasBootstrap = Number(ex.runningBootstrapFinishedAtMs || 0) > 0;
    const hasLogs = this._runningLogCount(ex) > 0;
    return !(hasStart && hasBootstrap && hasLogs);
  }

  private _runPhaseOverviewLine(ex: ProjectExecute): string {
    const prs = readProjectRunStatus(ex);
    const label = projectRunStatusLabel(prs).replace(/\.\.\.$/, "");
    if (prs === ProjectRunStatus.Initializing) {
      return "Not started yet (login and initialization in progress)";
    }
    if (prs === ProjectRunStatus.Running) {
      const parts: string[] = [label];
      const runFor = this._formatRunStartCompact(ex);
      if (runFor !== "—") parts.push(`run for ${runFor}`);
      const bootstrapDone = Number(ex.runningBootstrapFinishedAtMs || 0) > 0;
      const bootstrapPending =
        Number(ex.runningPhaseStartedAtMs || 0) > 0 && !bootstrapDone;
      parts.push(
        bootstrapDone
          ? "bootstrap yes"
          : bootstrapPending
            ? "bootstrap pending"
            : "bootstrap no",
      );
      const runningLogCount = this._runningLogCount(ex);
      if (runningLogCount > 0) {
        parts.push(`activity logs ${runningLogCount} (screenshot + summary)`);
      }
      const cycleAttempted = Math.max(0, Number(ex.nodeReportCycleAttempted || 0));
      const cycleUpdated = Math.max(0, Number(ex.nodeReportCycleUpdated || 0));
      const cycleNoNew = Math.max(0, Number(ex.nodeReportCycleNoNewFindings || 0));
      const cycleRevision = Math.max(
        0,
        Number(ex.nodeReportCycleRevision || (Number(ex.pfmRevision || 0) + 1)),
      );
      if (cycleAttempted > 0 || cycleUpdated > 0 || cycleNoNew > 0) {
        parts.push(
          `node learning cycle Rev ${cycleRevision}: attempted ${cycleAttempted}, updated ${cycleUpdated}, no new findings ${cycleNoNew}`,
        );
      }
      parts.push("activity ~2 min · delivery ~5–10 min (.pfm + .FMR)");
      parts.push("node limit: ≤12 target / 15 max per parent level");
      return parts.join(" · ");
    }
    return label;
  }

  private _finalStopStatusLabel(ex: ProjectExecute): string | null {
    const prs = readProjectRunStatus(ex);
    if (prs >= ProjectRunStatus.UserStopDone) {
      return projectRunStatusLabel(prs);
    }
    return null;
  }

  private _statusBadge(statusText: string): ReturnType<typeof html> {
    const color = STATUS_COLORS[String(statusText)] || "#838387";
    return html`<span
      class="status-badge"
      style="background:${color}22;color:${color};border:1px solid ${color}44"
      >${statusText}</span
    >`;
  }

  private _formatClock(ms: number): string {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  private _formatRunStartCompact(ex: ProjectExecute): string {
    const startedMs = Number(ex.runningPhaseStartedAtMs || 0);
    if (!Number.isFinite(startedMs) || startedMs <= 0) return "—";
    const nowMs = Date.now();
    const baseDuration =
      isLiveProjectExecute(ex)
        ? Math.max(0, nowMs - startedMs)
        : Math.max(0, Number(ex.durationMs || 0));
    const totalSec = Math.floor(baseDuration / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}'${String(s).padStart(2, "0")}"`;
  }

  /** Live wall-clock budget countdown while the run is active (updates every 1s). */
  private _renderActiveRunCountdown(ex: ProjectExecute): ReturnType<typeof html> | null {
    if (this._extendingRunId === ex.id) {
      return html`
        <span class="status-timer" title=${this._t("runs.extendingTitle")}>
          <span>${this._t("runs.extending")}</span>
        </span>
      `;
    }
    if (this._stoppingRunId === ex.id) {
      return html`
        <span class="status-timer status-timer--warn" title=${this._t("runs.stoppingTitle")}>
          <span>${this._t("runs.stopping")}</span>
        </span>
      `;
    }
    const budgetMin = Number(ex.timeBudgetMinutes || 0);
    const phase = this._displayStatusLabel(ex);
    const executorRunning = storedProjectRunStatus(ex) === ProjectRunStatus.Running;
    if (budgetMin <= 0) {
      return html`
        <span class="status-timer" title="Run phase">
          <span>${phase}</span>
        </span>
      `;
    }
    void this._headerClockTick;
    const budgetMs = budgetMin * 60 * 1000;
    const budgetStart = budgetCountdownStartMs(ex);

    // Before executor promotes to Running, show full allowance (budget does not count down yet).
    if (!executorRunning || budgetStart == null) {
      return html`
        <span
          class="status-timer"
          title="Budget countdown starts when executor sets phase to Running"
        >
          <span>${phase}</span>
          <span aria-hidden="true">·</span>
          <strong>${this._formatClock(budgetMs)}</strong>
        </span>
      `;
    }

    const elapsed = Math.max(0, Date.now() - budgetStart);
    const remainingMs = Math.max(0, budgetMs - elapsed);
    const warn = elapsed >= budgetMs * 0.85;
    const critical = elapsed >= budgetMs * 0.95;
    const cls = [
      "status-timer",
      warn ? "status-timer--warn" : "",
      critical ? "status-timer--critical" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return html`
      <span class=${cls} title="Time remaining in this run's budget">
        <span>${phase}</span>
        <span aria-hidden="true">·</span>
        <strong>${this._formatClock(remainingMs)}</strong>
      </span>
    `;
  }

  private _loginPhaseStatus(ex: ProjectExecute): "missing" | "pending" | "success" | "failed" {
    return resolveLoginPhaseStatus(ex);
  }

  /**
   * Post-login setup: defer only when there is no inherited / most-recent map to show yet.
   */
  private _shouldDeferPfmMindmap(ex: ProjectExecute): boolean {
    if (readProjectRunStatus(ex) !== ProjectRunStatus.Running) return false;
    if (resolveInitializationReviewStatus(ex) === "success") return false;
    return !this._hasReadablePfmMindmapDuringInit(ex);
  }

  /** True when a prior run (or this run) has a committed pfm-tree the gateway can render. */
  private _hasReadablePfmMindmapDuringInit(ex: ProjectExecute): boolean {
    const lineage = this._resolvePfmLineageForUi(ex);
    return lineage.lineageReady && (lineage.hasCurrentPfmMap || lineage.previousPfmRunNumber > 0);
  }

  private _activityText(entry: ProgressLogEntry): string {
    if (entry.kind !== "tool_use" || (entry.toolName || "") !== "report_running_step") {
      return (entry.text || "").trim();
    }
    const ti = (entry.toolInput || {}) as Record<string, unknown>;
    const title = String(ti.title || "").trim();
    const finding = String(ti.finding || "").trim();
    const nextDirection = String(ti.next_direction || "").trim();
    const reasoning = String(ti.reasoning || "").trim();
    if (title) {
      return formatOperatorActivityLine(title, { finding, nextDirection, reasoning }).display;
    }
    const observation = String(ti.observation || "").trim();
    const decision = String(ti.decision || "").trim();
    const description = String(ti.description || "").trim();
    const loginStatus = String(ti.login_phase_status || "").trim().toLowerCase();
    const loginSuccess = typeof ti.login_success === "boolean"
      ? (ti.login_success ? "success" : "failed")
      : "";
    const segments = [
      loginStatus ? `Login: ${loginStatus}` : "",
      !loginStatus && loginSuccess ? `Login: ${loginSuccess}` : "",
      observation ? `Observe: ${observation}` : "",
      finding ? `Find: ${finding}` : "",
      reasoning ? `Reason: ${reasoning}` : "",
      decision ? `Decide: ${decision}` : "",
      nextDirection ? `Next: ${nextDirection}` : "",
      description,
    ].filter(Boolean);
    return segments.join(" | ") || (entry.text || "").trim();
  }

  /** Operator-facing activity tail: hide bootstrap/inheritance dumps mirrored from the gateway. */
  private _isInternalActivityLogEntry(entry: ProgressLogEntry): boolean {
    const text = this._activityText(entry).trim();
    if (!text) return true;
    if (text.includes("[EAD-RUN-ACK:v1]")) return true;
    if (text.startsWith("## Run Introduction")) return true;
    if (text.startsWith("Inherited PFM node reports from prior execution")) return true;
    if (text.startsWith("Inherited template PFM node EAD reports loaded")) return true;
    if (text.startsWith("PFM inherited from run ")) return true;
    if (text.startsWith("PFM template context loaded ")) return true;
    if (text.includes("[PFM-PHASE1-CANONICAL-BASELINE:v1]")) return true;
    if (text.includes("[PFM-MINDMAP-CACHE:v1]")) return true;
    if (text.includes("[PFM-POST-LOGIN-SKILLS:v1]")) return true;
    if (text.startsWith("[Node-Report-Refresh]")) return true;
    if (text.startsWith("RUN SCOPE (this session only):")) return true;
    if (text.startsWith("HARD PROJECT TEMPLATE BOUNDARY:")) return true;
    return false;
  }

  private _chatPanelConfig(
    ex: ProjectExecute,
    options?: { forceReadonly?: boolean; disableStatusPolling?: boolean },
  ): ChatPanelConfig {
    const sk = resolveRunSessionKey(ex);
    // Keep chat lightweight; DB-backed mindmap/report is primary data path.
    const isLiveRun = isLiveProjectExecute(ex);
    const terminalChatEligible = this._isTerminalRunEligibleForChat(ex);
    const pollMs = isLiveRun ? 4_000 : 30_000;
    const loginAwaitingCredentials = isLiveRun && isAwaitingLoginCredentials(ex);
    const composerEnabled =
      !Boolean(options?.forceReadonly) &&
      ((isLiveRun && !ex.paused) || terminalChatEligible);

    const phaseLabel = this._displayStatusLabel(ex);
    const inLoginPhase =
      isLiveRun && readProjectRunStatus(ex) === ProjectRunStatus.Initializing;
    const next: ChatPanelConfig = {
      sessionKey: sk,
      sessionKeySource: isRunChatSessionLive(ex) ? "live" : "fallback",
      executionId: ex.id,
      title: `${(ex.name || "Untitled run").slice(0, 48)} - ${phaseLabel}`,
      runBriefingMarkdown: buildRunBriefingMarkdown(ex),
      readonly: Boolean(options?.forceReadonly) || Boolean(ex.paused && isLiveRun && !terminalChatEligible),
      composerEnabled,
      loginAwaitingCredentials: options?.disableStatusPolling ? false : loginAwaitingCredentials,
      loginGateHint: inLoginPhase ? loginGateOperatorHint(ex) : undefined,
      showTimestamps: true,
      showAvatars: true,
      pollIntervalMs: pollMs,
      statusPollIntervalMs: isLiveRun ? 4_000 : 15_000,
      enableStatusPolling: options?.disableStatusPolling
        ? false
        : isLiveRun || loginAwaitingCredentials,
    };

    /* Single default object avoids changing reference id when literals would — parent still passes
       new object literal; semantic equality prevents chat thrash inside ead-chat-panel. */
    return next;
  }

  render(): ReturnType<typeof html> {
    if (executionsStore.loading && executionsStore.executions.length === 0)
      return html`<div class="loading">Loading executions…</div>`;

    const selected = executionsStore.selectedExecution;
    if (!selected) {
      return html`
        <div class="empty">
          <strong>No run selected</strong>
          <span>Select a run from the left sidebar under Project run.</span>
        </div>
      `;
    }

    let detail: ReturnType<typeof html>;
    try {
      detail = this._renderDetail(selected);
    } catch (err) {
      console.warn("[runs-view] Failed to render rich run detail:", err);
      detail = this._renderDetailFallback(selected, err);
      const rid = String(selected.id || "").trim();
      if (rid && this._detailRenderRecoveryRunId !== rid) {
        this._detailRenderRecoveryRunId = rid;
        queueMicrotask(() => {
          void executionsStore.refreshSelectionDetail().finally(() => {
            this._detailRenderRecoveryRunId = "";
            this.requestUpdate();
          });
        });
      }
    }
    const isLiveRun = isLiveProjectExecute(selected);
    const terminalChatEligible = this._isTerminalRunEligibleForChat(selected);
    const canComposeChat =
      !this.forceReadonlyChat &&
      (isLiveRun || terminalChatEligible) &&
      !(selected.paused && isLiveRun);

    if (this.hideChatPanel) {
      return html`
        <div class="detail-panel" @scroll=${this._handleDetailScroll}>${detail}</div>
        ${this._renderActivityFullscreen()}
        ${this._renderRunConfirmModal()}
      `;
    }
    return html`
      <ead-split-panel-layout
        .splitRatio=${this._splitRatio}
        .minLeftWidth=${280}
        .minRightWidth=${320}
        .minSplitRatio=${0.3}
        .maxSplitRatio=${0.7}
        .collapsible=${false}
      >
        <div slot="left" class="left-pane-slot">
          <div class="detail-panel" @scroll=${this._handleDetailScroll}>${detail}</div>
        </div>

        <div slot="right" class="chat-slot">
          <ead-chat-panel
            .config=${this._chatPanelConfig(selected, {
              forceReadonly: !canComposeChat,
              disableStatusPolling: !canComposeChat,
            })}
          ></ead-chat-panel>
        </div>
      </ead-split-panel-layout>
      ${this._renderActivityFullscreen()}
      ${this._renderRunConfirmModal()}
    `;
  }

  private _renderDetailFallback(ex: ProjectExecute, err: unknown): ReturnType<typeof html> {
    const message = err instanceof Error ? err.message : String(err || "Unknown render error");
    console.warn("[runs-view] Run detail fallback:", message);
    return html`
      <div class="detail-header">
        ${this._renderDetailTabs()}
        <div class="detail-actions">
          <button
            class="btn btn-sm btn-ghost detail-refresh-btn"
            type="button"
            title="Refresh"
            aria-label="Refresh run detail"
            @click=${() => void executionsStore.refreshSelectionDetail()}
          >
            <span style="display:inline-flex;width:16px;height:16px;">${icons.refresh}</span>
          </button>
        </div>
      </div>
      <div class="detail-body">
        <div class="banner banner--invalid" role="status">
          <span style="display:inline-flex;width:16px;height:16px;flex:0 0 auto;margin-top:1px;">${icons.helpCircle}</span>
          <span>
            The full run view is temporarily unavailable. Your run data is safe — use Refresh above or reload the page.
          </span>
        </div>
        <section class="section-block">
          <div class="section-title">Run overview</div>
          <dl class="kv-grid" style="grid-template-columns:auto 1fr;max-width:640px;">
            <dt>Status</dt>
            <dd>${ex.status || "—"}</dd>
            <dt>Target</dt>
            <dd>${ex.targetUrl?.trim() || "—"}</dd>
            <dt>Started</dt>
            <dd>${ex.startTime != null ? formatMs(Number(ex.startTime)) : "—"}</dd>
            <dt>PFM nodes</dt>
            <dd>${(ex.results || []).length}</dd>
            <dt>Reports</dt>
            <dd>${ex.reportsCount ?? (ex.reports || []).length}</dd>
            <dt>Activity entries</dt>
            <dd>${ex.progressLogCount ?? (ex.progressLog || []).length}</dd>
          </dl>
        </section>
      </div>
    `;
  }

  private _renderRunConfirmModal(): ReturnType<typeof html> {
    const state = this._confirmRunAction;
    if (!state) return html``;

    const { action, run } = state;
    const base = String(run.name || run.id || "").replace(/^Run:\s*/i, "").trim() || run.id;
    const runNumber = Number(run.runNumber || 0);
    const label = Number.isFinite(runNumber) && runNumber > 0
      ? `Run ${runNumber}, ${base}`
      : base;

    if (action === "finish" || action === "cancel") {
      const isFinish = action === "finish";
      return html`
        <div
          class="confirm-backdrop"
          @click=${() => {
            this._confirmRunAction = null;
          }}
        >
          <div
            class="confirm-card ${isFinish ? "confirm-card--finish" : ""}"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stop-run-confirm-title"
            @click=${(event: Event) => event.stopPropagation()}
          >
            <div class="confirm-header">
              <div class="confirm-icon ${isFinish ? "confirm-icon--finish" : ""}">
                ${isFinish ? "✓" : "!"}
              </div>
              <div>
                <h3 id="stop-run-confirm-title" class="confirm-title">
                  ${isFinish ? this._t("runs.confirm.finishTitle") : this._t("runs.confirm.cancelTitle")}
                </h3>
                <div class="confirm-subtitle">${label}</div>
              </div>
            </div>
            <div class="confirm-body">
              <div>
                ${isFinish
                  ? this._t("runs.confirm.finishBody")
                  : this._t("runs.confirm.cancelBody")}
              </div>
              ${isFinish
                ? html`
                    <div class="confirm-note">
                      ${this._t("runs.confirm.finishNote")}
                    </div>
                  `
                : ""}
              ${this._confirmRunError
                ? html`<div class="confirm-error">${this._t("runs.confirm.error.stop", { error: this._confirmRunError })}</div>`
                : ""}
            </div>
            <div class="confirm-actions">
              <button
                class="btn btn-sm"
                type="button"
                @click=${() => {
                  this._confirmRunAction = null;
                }}
              >
                ${this._t("runs.confirm.notNow")}
              </button>
              <button
                class="btn btn-sm ${isFinish ? "" : "btn-danger"}"
                type="button"
                @click=${() => void this._stopExecution(run, action)}
              >
                ${isFinish ? this._t("runs.confirm.yesFinish") : this._t("runs.confirm.yesCancel")}
              </button>
            </div>
          </div>
        </div>
      `;
    }

    const sameTemplateRuns = this._runsForTemplate(run.linkedTemplateId);
    const validTrainingRuns = this._validTrainingRunsForTemplate(run.linkedTemplateId);
    const isLastRunForTemplate = sameTemplateRuns.length <= 1;
    const isCurrentTrainingSource =
      isExecutionEligibleForPfmInheritance(run) &&
      validTrainingRuns[0]?.id === run.id;
    const isOnlyValidTrainingRun =
      isExecutionEligibleForPfmInheritance(run) &&
      validTrainingRuns.length <= 1;

    const title = action === "delete" ? this._t("runs.confirm.deleteTitle") : this._t("runs.confirm.invalidateTitle");
    const primaryLabel = action === "delete" ? this._t("runs.deleteRun") : this._t("runs.markInvalid");
    const explanation = action === "delete"
      ? this._t("runs.confirm.deleteBody")
      : this._t("runs.confirm.invalidateBody");
    const warning = isLastRunForTemplate
      ? this._t("runs.confirm.warning.lastRun")
      : isOnlyValidTrainingRun
        ? this._t("runs.confirm.warning.onlyValid")
        : isCurrentTrainingSource
          ? this._t("runs.confirm.warning.currentSource")
          : this._t("runs.confirm.warning.keepLatest");

    return html`
      <div class="confirm-backdrop" @click=${() => { this._confirmRunAction = null; }}>
        <div class="confirm-card" role="dialog" aria-modal="true" @click=${(event: Event) => event.stopPropagation()}>
          <div class="confirm-header">
            <div class="confirm-icon">!</div>
            <div>
              <h3 class="confirm-title">${title}</h3>
              <div class="confirm-subtitle">${label}</div>
            </div>
          </div>
          <div class="confirm-body">
            <div>${explanation}</div>
            <div class="confirm-warning">${warning}</div>
            ${this._confirmRunError
              ? html`<div class="confirm-error">${this._t("runs.confirm.error.actionFailed", { error: this._confirmRunError })}</div>`
              : ""}
          </div>
          <div class="confirm-actions">
            <button class="btn btn-sm" type="button" @click=${() => { this._confirmRunAction = null; }}>
              ${this._t("projects.cancel")}
            </button>
            <button
              class="btn btn-sm btn-danger"
              type="button"
              @click=${() =>
                action === "delete"
                  ? void this._deleteExecution(run)
                  : void this._invalidateExecution(run)}
            >
              ${primaryLabel}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderDetailTabs(): ReturnType<typeof html> {
    return html`
      <div class="left-tabs" role="tablist" aria-label="Run detail view tabs">
        <button
          class="left-tab ${this._leftTab === "explore" ? "left-tab--active" : ""}"
          type="button"
          role="tab"
          aria-selected=${this._leftTab === "explore"}
          @click=${() => {
            this._setLeftTab("explore");
          }}
        >
          ${this._t("runs.tabs.explore")}
        </button>
        <button
          class="left-tab ${this._leftTab === "activity" ? "left-tab--active" : ""}"
          type="button"
          role="tab"
          aria-selected=${this._leftTab === "activity"}
          @click=${() => {
            this._setLeftTab("activity");
          }}
        >
          ${this._t("runs.tabs.activity")}
        </button>
      </div>
    `;
  }

  private _renderRunningPhaseGuidanceBanner(ex: ProjectExecute): ReturnType<typeof html> | null {
    if (readProjectRunStatus(ex) !== ProjectRunStatus.Running) return null;
    return html`
      <div class="banner banner--info" role="status">
        ${this._t("runs.runningPhaseBanner")}
      </div>
    `;
  }

  private _renderPfmDeliveryValidationBanner(ex: ProjectExecute): ReturnType<typeof html> | null {
    if (ex.id !== this._deliveryValidationRunId) return null;
    const banner = buildPfmDeliveryValidationBanner(this._deliveryValidation);
    if (!banner?.show) return null;
    const title = this._t(banner.titleKey) || banner.titleFallback;
    const role = banner.severity === "error" ? "alert" : "status";
    const className =
      banner.severity === "error"
        ? "banner banner--error"
        : banner.severity === "warning"
          ? "banner banner--warning"
          : banner.severity === "success"
            ? "banner banner--success"
            : "banner banner--info";
    return html`
      <div class=${className} role=${role}>
        <span class="banner__title">${title}</span>
        <span>${banner.message}</span>
        ${banner.detail
          ? html`<span class="banner__detail">${banner.detail}</span>`
          : nothing}
      </div>
    `;
  }

  private _renderDetail(ex: ProjectExecute): ReturnType<typeof html> {
    const rawResults = ex.results || [];
    const isActive = isLiveProjectExecute(ex);
    const progressLog = ex.progressLog ?? [];

    const statusLine = this._displayStatusLabel(ex);
    const finalStopLabel = this._finalStopStatusLabel(ex);

    const started =
      ex.startTime != null
        ? formatMs(Number(ex.startTime))
        : "—";

    const duration =
      isActive && ex.startTime != null
        ? formatDurationHuman(Date.now() - ex.startTime)
        : formatDurationHuman(ex.durationMs ?? 0);
    const runPhaseLine = this._runPhaseOverviewLine(ex);
    const showRunPhaseRow = readProjectRunStatus(ex) !== ProjectRunStatus.Initializing;

    const loginPhaseStatus = this._loginPhaseStatus(ex);
    const loginSuccessful = loginPhaseStatus === "success";
    const results = loginSuccessful ? rawResults : [];

    return html`
      <div class="detail-header">
        ${this._renderDetailTabs()}
        <div class="detail-actions">
          <button
            class="btn btn-sm btn-ghost detail-refresh-btn"
            type="button"
            title=${this._t("runs.refresh")}
            aria-label=${this._t("runs.refresh")}
            @click=${() => void executionsStore.refreshSelectionDetail()}
          >
            <span style="display:inline-flex;width:16px;height:16px;">${icons.refresh}</span>
          </button>
          ${isActive ? this._renderActiveRunCountdown(ex) ?? "" : ""}
          ${isActive && this._stoppingRunId !== ex.id && this._extendingRunId !== ex.id
            ? html`
                <div style="position:relative;">
                  <button
                    class="btn btn-sm btn-danger"
                    type="button"
                    @click=${() => {
                      this._runOverflowMenuFor = null;
                      this._stopMenuOpenFor = this._stopMenuOpenFor === ex.id ? null : ex.id;
                    }}
                  >
                    ${this._t("runs.menu")} ▾
                  </button>
                  ${this._stopMenuOpenFor === ex.id
                    ? html`
                        <div
                          style="position:absolute;right:0;top:calc(100% + 6px);min-width:176px;border:1px solid var(--border);border-radius:8px;background:var(--bg-elevated,#1b1f2a);box-shadow:0 12px 24px rgba(0,0,0,.28);overflow:hidden;z-index:8;"
                        >
                          <button
                            class="btn"
                            style="display:block;width:100%;text-align:left;border:none;border-radius:0;background:transparent;padding:8px 10px;"
                            type="button"
                            @click=${() => {
                              void this._extendRunBudget(ex, 15);
                            }}
                          >
                            ${this._t("runs.extend15")}
                          </button>
                          <button
                            class="btn"
                            style="display:block;width:100%;text-align:left;border:none;border-radius:0;background:transparent;padding:8px 10px;"
                            type="button"
                            @click=${() => {
                              void this._extendRunBudget(ex, 30);
                            }}
                          >
                            ${this._t("runs.extend30")}
                          </button>
                          <button
                            class="btn"
                            style="display:block;width:100%;text-align:left;border:none;border-radius:0;background:transparent;padding:8px 10px;"
                            type="button"
                            @click=${() => {
                              void this._extendRunBudget(ex, 60);
                            }}
                          >
                            ${this._t("runs.extend60")}
                          </button>
                          <div style="height:1px;background:color-mix(in srgb,var(--border) 70%,transparent);margin:2px 0;"></div>
                          <button
                            class="btn"
                            style="display:block;width:100%;text-align:left;border:none;border-radius:0;background:transparent;padding:8px 10px;"
                            type="button"
                            @click=${() => {
                              this._stopMenuOpenFor = null;
                              this._openRunConfirm("finish", ex);
                            }}
                          >
                            ${this._t("runs.stopDone")}
                          </button>
                          <button
                            class="btn"
                            style="display:block;width:100%;text-align:left;border:none;border-radius:0;background:transparent;padding:8px 10px;"
                            type="button"
                            @click=${() => {
                              this._stopMenuOpenFor = null;
                              this._openRunConfirm("cancel", ex);
                            }}
                          >
                            ${this._t("runs.stopCancel")}
                          </button>
                        </div>
                      `
                    : ""}
                </div>
              `
            : finalStopLabel
              ? this._statusBadge(finalStopLabel)
              : ""}
          ${!isActive
            ? html`
                <div style="position:relative;">
                  <button
                    class="btn btn-sm btn-ghost"
                    type="button"
                    aria-label=${this._t("runs.moreActions")}
                    title=${this._t("runs.deleteOrInvalid")}
                    @click=${() => {
                      this._stopMenuOpenFor = null;
                      this._runOverflowMenuFor = this._runOverflowMenuFor === ex.id ? null : ex.id;
                    }}
                  >
                    ⋯
                  </button>
                  ${this._runOverflowMenuFor === ex.id
                    ? html`
                        <div class="overflow-menu-panel" role="menu">
                          <button
                            type="button"
                            class="overflow-menu-danger"
                            role="menuitem"
                            @click=${() => this._openRunConfirm("delete", ex)}
                          >
                            ${this._t("runs.deleteRun")}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            ?disabled=${ex.validForDataReportingTraining === false}
                            @click=${() => this._openRunConfirm("invalidate", ex)}
                          >
                            ${ex.validForDataReportingTraining === false
                              ? this._t("runs.invalidAlready")
                              : this._t("runs.markInvalid")}
                          </button>
                        </div>
                      `
                    : ""}
                </div>
              `
            : ""}
        </div>
      </div>
      <div class="detail-body">
        ${ex.validForDataReportingTraining === false
          ? html`
              <div class="banner banner--invalid">
                <span aria-hidden="true">${icons.circleSlash}</span>
                <span>
                  <strong>${this._t("runs.invalidBanner")}</strong>
                  ${ex.invalidForDataReportingTrainingReason || ex.learningExclusionReason
                    ? html` ${ex.invalidForDataReportingTrainingReason || ex.learningExclusionReason}`
                    : ""}
                </span>
              </div>
            `
          : ""}
        ${this._stopRunError
          ? html`<div class="banner banner--error" role="alert">
              ${this._t("runs.error.stopRun", { error: this._stopRunError })}
            </div>`
          : ""}
        ${ex.paused
          ? html`
              <div class="banner banner--paused">
                ${this._t("runs.pausedBanner")}
              </div>
            `
          : ""}
        ${ex.timeBudgetExceeded
          ? html`<div class="banner banner--budget">
              ${this._t("runs.budgetExceeded")}
            </div>`
          : ""}
        ${ex.lastErrorMessage?.trim()
          ? html`<div class="banner banner--error">
              ${ex.lastErrorMessage}
            </div>`
          : ""}
        ${this._renderRunningPhaseGuidanceBanner(ex) ?? ""}
        ${this._renderPfmDeliveryValidationBanner(ex) ?? ""}

        ${this._leftTab === "explore" ? html`
        <section class="section-block">
          <div class="section-title">${this._t("runs.overview")}</div>
          <dl class="kv-grid" style="grid-template-columns:auto 1fr;max-width:520px;">
            ${!isActive && statusLine && statusLine !== "Done"
              ? html`
                  <dt>${this._t("runs.status")}</dt>
                  <dd>${statusLine}</dd>
                `
              : ""}
            <dt>${this._t("runs.loginPhase")}</dt>
            <dd>${formatLoginPhaseDisplay(ex, loginPhaseStatus)}</dd>
            ${showRunPhaseRow
              ? html`
                  <dt>${this._t("runs.runPhase")}</dt>
                  <dd>${runPhaseLine}</dd>
                `
              : ""}
            <dt>${this._t("runs.runNumber")}</dt>
            <dd>${this._formatRunNumberOverview(ex)}</dd>
            <dt>${this._t("runs.started")}</dt>
            <dd>${started}</dd>
            <dt>${this._t("runs.timeBudget")}</dt>
            <dd>${ex.timeBudgetMinutes ? `${ex.timeBudgetMinutes} min` : "—"}</dd>
            ${!isActive
              ? html`
                  <dt>${this._t("runs.duration")}</dt>
                  <dd>${duration}</dd>
                `
              : ""}
            <dt>${this._t("runs.target")}</dt>
            <dd>${ex.targetUrl?.trim() || "—"}</dd>
            ${(ex.description || "").trim()
              ? html`
                  <dt>${this._t("runs.mission")}</dt>
                  <dd style="white-space:pre-wrap;">${(ex.description || "").trim()}</dd>
                `
              : ""}
            <dt>${this._t("runs.aiAnswerLanguage")}</dt>
            <dd>
              <ead-ai-answer-language-field
                .value=${this._answerLanguageValue(ex)}
                .locale=${this.locale}
                .pickable=${isLiveProjectExecute(ex)}
                ?disabled=${this._answerLanguageUpdatingRunId === ex.id}
                @language-change=${(event: CustomEvent<{ value: AiAnswerLanguage }>) =>
                  void this._setExecutionAnswerLanguage(ex, event.detail.value)}
              ></ead-ai-answer-language-field>
            </dd>
          </dl>
          ${typeof ex.logTokens === "number" && Number.isFinite(ex.logTokens)
            ? html`<p style="margin:12px 0 0;font-size:12px;color:var(--muted);">
                ${this._t("runs.transcriptHint", { tokens: ex.logTokens.toLocaleString() })}
              </p>`
            : ""}
          ${ex.cancelReason?.trim()
            ? html`<p style="margin:12px 0 0;font-size:12px;color:var(--muted);">
                <strong>${this._t("runs.stopNote", { note: ex.cancelReason })}</strong>
              </p>`
            : ""}
        </section>

        ${readProjectRunStatus(ex) === ProjectRunStatus.Initializing
          ? this._renderPfmWaitingForLoginSection(ex)
          : this._shouldDeferPfmMindmap(ex)
            ? this._renderPfmMindmapInitializationPlaceholder(ex)
            : html`
                ${this._renderCanonicalPfmPromotionCard(ex)}
                ${this._renderPfmExploreStack(ex, results)}
              `}

        ${this._renderRecentActivityProgress(ex)}
          ` : this._renderActivityTab(ex)}
      </div>
    `;
  }

  private _normalizeActivityScreenshot(
    screenshot: RunScreenshot,
    executionId?: string,
  ): RunScreenshot {
    const id = String(executionId || executionsStore.selectedExecution?.id || "").trim();
    const url = resolveExecutionReportUrl(screenshot.url, id);
    const thumbnailUrl = resolveExecutionReportUrl(screenshot.thumbnailUrl || screenshot.url, id);
    return { ...screenshot, url: url || thumbnailUrl, thumbnailUrl: thumbnailUrl || url };
  }

  private _renderActivityFeedRows(
    activities: RunActivity[],
    executionId?: string,
  ): ReturnType<typeof html> {
    const exId = String(executionId || executionsStore.selectedExecution?.id || "").trim();
    return html`${activities.map((activity) => html`
      <div class="activity-row">
        <div class="activity-time">${this._formatClockTime(activity.timestamp)}</div>
        <div class="activity-text">${this._renderActivityText(activity)}</div>
        ${(() => {
          const visibleShots = activity.screenshots
            .map((rawShot) => this._normalizeActivityScreenshot(rawShot, exId))
            .filter((shot) => String(shot.url || shot.thumbnailUrl || "").trim());
          return visibleShots.length
          ? html`
              <div class="activity-inline-images">
                ${visibleShots.map((screenshot) => {
                  const expandedKey = `${activity.fingerprint}:${screenshot.fingerprint}`;
                  const expanded = this._expandedActivityImageKey === expandedKey;
                  const broken = this._brokenActivityImageKeys.has(expandedKey);
                  const preferredSrc = expanded
                    ? screenshot.url
                    : screenshot.thumbnailUrl || screenshot.url;
                  const src = this._activityImageFallbackSrc.get(expandedKey) || preferredSrc;
                  if (!src || broken) return "";
                  return html`
                    <div class="activity-inline-thumb ${expanded ? "activity-inline-thumb--expanded" : ""}">
                      <div class="activity-thumb-image-wrap">
                        <button
                          class="activity-inline-thumb-main"
                          type="button"
                          aria-expanded=${expanded}
                          @click=${() => {
                            this._expandedActivityImageKey = expanded ? "" : expandedKey;
                          }}
                        >
                          <img
                            src=${src}
                            alt=${screenshot.caption || this._t("runs.screenshot")}
                            @error=${() => {
                              const fullUrl = screenshot.url || screenshot.thumbnailUrl || "";
                              if (fullUrl && src !== fullUrl) {
                                this._activityImageFallbackSrc = new Map(
                                  this._activityImageFallbackSrc,
                                ).set(expandedKey, fullUrl);
                                return;
                              }
                              this._brokenActivityImageKeys = new Set(
                                this._brokenActivityImageKeys,
                              ).add(expandedKey);
                            }}
                          />
                        </button>
                        <button
                          class="activity-fullscreen-btn"
                          type="button"
                          title=${this._t("runs.activity.viewFullscreen")}
                          aria-label=${this._t("runs.activity.viewFullscreen")}
                          @click=${(event: MouseEvent) => {
                            event.stopPropagation();
                            this._fullscreenActivityImage = {
                              url: screenshot.url,
                              caption: screenshot.caption || this._t("runs.screenshot"),
                              activityText: this._activityDisplayLine(activity),
                              timestamp: activity.timestamp,
                            };
                          }}
                        >
                          ${this._maximizeIcon()}
                        </button>
                      </div>
                      <button
                        class="activity-inline-thumb-main"
                        type="button"
                        aria-expanded=${expanded}
                        @click=${() => {
                          this._expandedActivityImageKey = expanded ? "" : expandedKey;
                        }}
                      >
                        <div class="ss-thumb-time">
                          ${expanded ? this._t("runs.activity.collapse") : this._t("runs.activity.expand")}
                        </div>
                      </button>
                    </div>
                  `;
                })}
              </div>
            `
          : "";
        })()}
      </div>
    `)}`;
  }

  private _renderRecentActivityProgress(ex: ProjectExecute): ReturnType<typeof html> {
    const activities = this._runActivitiesForDisplay(ex);
    const total = activities.length;
    if (!total) return html``;

    const recent = activities.slice(0, 20);
    return html`
      <section class="section-block">
        <div class="section-title">${this._t("runs.recentActivity")}</div>
        <div class="ss-layout">
          <div class="activity-feed">
            ${this._renderActivityFeedRows(recent, ex.id)}
          </div>
        </div>
        ${total > 20
          ? html`
              <button
                class="activity-view-more"
                type="button"
                @click=${() => this._setLeftTab("activity")}
              >
                ${this._t("runs.viewMore", { count: total })}
              </button>
            `
          : ""}
      </section>
    `;
  }

  private _renderActivityTab(ex: ProjectExecute): ReturnType<typeof html> {
    const activities = this._runActivitiesForDisplay(ex);
    const cycleAttempted = Math.max(0, Number(ex.nodeReportCycleAttempted || 0));
    const cycleUpdated = Math.max(0, Number(ex.nodeReportCycleUpdated || 0));
    const cycleNoNew = Math.max(0, Number(ex.nodeReportCycleNoNewFindings || 0));
    const cycleRevision = Math.max(0, Number(ex.nodeReportCycleRevision || (Number(ex.pfmRevision || 0) + 1)));
    const totalAttempted = Math.max(0, Number(ex.nodeReportTotalAttempted || 0));
    const totalUpdated = Math.max(0, Number(ex.nodeReportTotalUpdated || 0));
    const totalNoNew = Math.max(0, Number(ex.nodeReportTotalNoNewFindings || 0));
    const trend = this._nodeLearningTrend(cycleAttempted, cycleUpdated, cycleNoNew);
    const screenshotCount = activities.reduce((n, a) => n + a.screenshots.length, 0);
    return html`
      <section class="section-block activity-tab-only">
        <div class="section-title">
          ${this._t("runs.activitySummary", {
            entries: activities.length,
            entryWord: activities.length === 1 ? this._t("runs.entry") : this._t("runs.entries"),
            screenshots: screenshotCount
              ? ` · ${screenshotCount} ${screenshotCount === 1 ? this._t("runs.screenshot") : this._t("runs.screenshots")}`
              : "",
          })}
        </div>
        <p class="activity-tab-hint">
          ${this._t("runs.activityTargetHint")}
        </p>
        <div class="node-learning-card">
          <div class="node-learning-head">
            <span class="node-learning-title">Node Learning Cycle · Rev ${cycleRevision}</span>
            <span class="node-learning-trend">Trend: ${trend}</span>
          </div>
          <div class="node-learning-metrics">
            <span class="node-learning-chip">Attempted: ${cycleAttempted}</span>
            <span class="node-learning-chip">Updated: ${cycleUpdated}</span>
            <span class="node-learning-chip">No new findings: ${cycleNoNew}</span>
          </div>
          <div class="node-learning-totals">
            Run totals - attempted ${totalAttempted}, updated ${totalUpdated}, no new findings ${totalNoNew}
          </div>
        </div>
        ${!activities.length
          ? html`
              <div class="activity-empty">
                <div class="activity-empty-title">${this._t("runs.noActivityYet")}</div>
                <p>
                  ${this._t("runs.activityHint")}
                </p>
              </div>
            `
          : ""}
        ${activities.length
          ? html`
              <div class="ss-layout">
                <div class="activity-feed">
                  ${this._renderActivityFeedRows(activities, ex.id)}
                </div>
              </div>
            `
          : ""}
      </section>
    `;
  }

  private _nodeLearningTrend(
    attempted: number,
    updated: number,
    noNewFindings: number,
  ): string {
    if (attempted <= 0) return "No attempts yet";
    if (updated <= 0 && noNewFindings > 0) return "Stable baseline";
    if (updated >= attempted) return "Strong improvement";
    const ratio = updated / Math.max(attempted, 1);
    if (ratio >= 0.5) return "Improving";
    return "Mixed";
  }

  private _pfmArtifactReadExecutionId(ex: ProjectExecute): string {
    if (ex.pfmHasCommittedSnapshot === true) return "";
    const read = this._resolvedPfmTreeReadExecutionId(ex);
    if (read && read !== ex.id) return read;
    const b = this._resolvedPfmBaselineExecutionId(ex);
    if (!b || b === ex.id) return "";
    return b;
  }

  private _deriveTemplateBaselineExecutionId(ex: ProjectExecute): string {
    const templateId = (ex.linkedTemplateId || "").trim();
    if (!templateId) return "";
    const peers = this._runsForTemplate(templateId).map((run) => ({
      id: run.id,
      runNumber: run.runNumber,
      pfmRevision: run.pfmRevision,
      pfmHasCommittedSnapshot: run.pfmHasCommittedSnapshot,
      status: run.status,
    }));
    const latest = findLatestTemplatePfmTreeRun(peers, String(ex.id || "").trim());
    return String(latest?.id || "").trim();
  }

  private _resolvedPfmBaselineExecutionId(ex: ProjectExecute): string {
    if (
      ex.pfmHasCommittedSnapshot === true
      && String(ex.pfmMapDisplayMode || "").trim().toLowerCase() !== "previous"
    ) {
      return "";
    }
    const baseline = String(ex.pfmBaselineExecutionId || "").trim();
    if (baseline && baseline !== ex.id) return baseline;
    const treeRead = String(ex.pfmTreeReadExecutionId || "").trim();
    if (treeRead && treeRead !== ex.id) return treeRead;
    return this._deriveTemplateBaselineExecutionId(ex);
  }

  private _resolvedPfmTreeReadExecutionId(ex: ProjectExecute): string {
    const eid = String(ex.id || "").trim();
    if (this._runHasOwnMindmap(ex)) return eid;
    const treeRead = String(ex.pfmTreeReadExecutionId || "").trim();
    if (treeRead) return treeRead;
    const ownRev = Math.max(0, Number(ex.pfmRevision ?? 0));
    if (
      ex.pfmMapDisplayMode === "current"
      && (ex.pfmHasCommittedSnapshot === true || ownRev > 0)
    ) {
      return eid;
    }
    const baseline = this._resolvedPfmBaselineExecutionId(ex);
    return baseline && baseline !== eid ? baseline : "";
  }

  private async _promoteOperatorCanonical(ex: ProjectExecute): Promise<void> {
    const tid = (ex.linkedTemplateId || "").trim();
    if (!tid || !ex.id) return;
    if (!confirm("Set this run as the template’s canonical (most accurate) EAD feature map baseline?")) return;
    const done = await hermesClient.request("templates.setCanonicalPfm", {
      templateId: tid,
      executionId: ex.id,
      source: "operator",
      rationale: "operator UI — set as template canonical PFM",
    });
    if (done === undefined) {
      notifyOperator({
        title: "Canonical update failed",
        message: hermesClient.getLastRequestError() || "Could not promote canonical PFM.",
        type: "error",
        modal: true,
      });
      return;
    }
    await executionsStore.refresh();
  }

  private async _reevaluateCanonicalPfm(ex: ProjectExecute): Promise<void> {
    if (!ex.id) return;
    const done = await hermesClient.request("pfm.evaluateCanonical", { executionId: ex.id, force: true });
    if (done === undefined || (done as { ok?: boolean }).ok === false) {
      notifyOperator({
        title: "Canonical evaluation failed",
        message: hermesClient.getLastRequestError() || "Evaluation did not complete.",
        type: "error",
        modal: true,
      });
      return;
    }
    await executionsStore.refresh();
  }

  private _renderCanonicalPfmPromotionCard(_ex: ProjectExecute): ReturnType<typeof html> {
    return html``;
  }

  private _renderPfmWaitingForLoginSection(ex: ProjectExecute): ReturnType<typeof html> {
    return html`
      <section class="section-block">
        <div class="section-title">
          <span>${this._pfmMindmapSectionTitle(ex)}</span>
        </div>
        <div
          style="padding:28px;text-align:center;border:1px dashed color-mix(in srgb,var(--border) 60%,transparent);border-radius:var(--radius-md);color:var(--muted);font-size:13px;line-height:1.6;"
          role="status"
        >
          <strong>Phase 1 — Initializing (login required)</strong><br />
          Complete login before the run enters Phase 2 (Running).<br />
          ${loginGateOperatorHint(ex)}
        </div>
      </section>
    `;
  }

  private _renderPfmMindmapInitializationPlaceholder(ex: ProjectExecute): ReturnType<typeof html> {
    return html`
      <section class="section-block">
        <div class="section-title">
          <span>${this._pfmMindmapSectionTitle(ex)}</span>
        </div>
        <div
          style="padding:28px;text-align:center;border:1px dashed color-mix(in srgb,var(--border) 60%,transparent);border-radius:var(--radius-md);color:var(--muted);font-size:13px;line-height:1.6;"
          role="status"
        >
          Phase 2 — Running: ${this._t("runs.runningPhaseInitPending")}
        </div>
      </section>
    `;
  }

  private _togglePfmExploreMaximized(): void {
    const next = !this._pfmExploreMaximized;
    this._pfmExploreMaximized = next;
    if (next) this._selectedNodeReportCollapsed = false;
  }

  private _renderPfmExploreHeaderActions(ex: ProjectExecute): ReturnType<typeof html> {
    const loginSuccessful = this._loginPhaseStatus(ex) === "success";
    const showLearningToggle = shouldShowContinuousLearningToggle(ex, { loginSuccessful });
    const maximized = this._pfmExploreMaximized;
    return html`
      ${showLearningToggle
        ? html`
            <ead-pfm-interactive-learning-toggle
              .executionId=${ex.id}
              .enabled=${isExecutionValidForInteractiveLearning(ex)}
              .hasOwnPfmMap=${ex.hasDeliveredPfmMapReport === true}
              .runActive=${isLiveProjectExecute(ex)}
              .pfmRevision=${ex.pfmRevision ?? 0}
              @pfm-mindmap-optimize-requested=${() => this._onPfmMindmapOptimizeRequested()}
            ></ead-pfm-interactive-learning-toggle>
          `
        : ""}
      <button
        class="pfm-explore-icon-btn"
        type="button"
        title=${maximized ? this._t("runs.pfm.exitMaximize") : this._t("runs.pfm.maximize")}
        aria-label=${this._t("runs.pfm.maximizeAria")}
        @click=${() => this._togglePfmExploreMaximized()}
      >
        <span>${maximized ? icons.minimize : icons.maximize}</span>
      </button>
    `;
  }

  private _renderPfmExploreStack(
    ex: ProjectExecute,
    results: EadFmNodeRun[],
  ): ReturnType<typeof html> {
    if (this._shouldDeferPfmMindmap(ex)) {
      return this._renderPfmMindmapInitializationPlaceholder(ex);
    }
    const initPending =
      readProjectRunStatus(ex) === ProjectRunStatus.Running
      && resolveInitializationReviewStatus(ex) !== "success";
    const stackClass = this._pfmExploreMaximized
      ? "pfm-explore-stack pfm-explore-stack--maximized"
      : "pfm-explore-stack";

    return html`
      <div
        class=${stackClass}
        role=${this._pfmExploreMaximized ? "dialog" : nothing}
        aria-modal=${this._pfmExploreMaximized ? "true" : nothing}
      >
        <div class="pfm-explore-stack__header">
          <span class="section-title">${this._pfmMindmapSectionTitle(ex)}</span>
          <div class="pfm-explore-stack__actions">${this._renderPfmExploreHeaderActions(ex)}</div>
        </div>
        ${initPending
          ? html`<p style="margin:0 0 8px;font-size:12px;line-height:1.45;color:var(--muted);">
              Post-login setup in progress — showing the saved map below until this run publishes its own.
            </p>`
          : ""}
        ${this._renderMindmapBreadcrumb(ex)}
        <div class="pfm-explore-stack__body">
          ${this._renderPfmMindmapContent(ex, results)}
          <div class="pfm-explore-stack__report-host">${this._renderSelectedNodeReport(ex)}</div>
        </div>
      </div>
    `;
  }

  private _renderPfmMindmapContent(
    ex: ProjectExecute,
    results: EadFmNodeRun[],
  ): ReturnType<typeof html> {
    const mindmapLineage = this._mindmapLineageForRun(ex);
    const pfmLineage = mindmapLineage.lineage;
    const pfmHeaderLabel = this._pfmMindmapSectionTitle(ex);
    const displayMode = mindmapLineage.displayMode;
    return html`
      <div class="pfm-explore-stack__mindmap-host">
        <ead-pfm-mindmap-mermaid
          class="current-context-mindmap"
          .suppressInternalMaximize=${true}
          .nodes=${results}
          .executionId=${ex.id}
          .pfmBaselineExecutionId=${this._resolvedPfmBaselineExecutionId(ex)}
          .pfmTreeReadExecutionId=${mindmapLineage.treeReadExecutionId}
          .pfmHasCommittedSnapshot=${mindmapLineage.hasCommittedSnapshot}
          .hasDeliveredPfmMapReport=${ex.hasDeliveredPfmMapReport === true}
          .pfmMapDisplayMode=${displayMode}
          .pfmSourceRunNumber=${pfmLineage.previousPfmRunNumber || ex.pfmSourceRunNumber || 0}
          .pfmBaselineTreeVersion=${pfmLineage.previousPfmRunNumber || (ex.pfmBaselineTreeVersion ?? 0)}
          .runStatus=${ex.status || ""}
          .pfmSnapshotPhase=${ex.pfmSnapshotPhase || ""}
          .pfmGenerationVersion=${ex.pfmGenerationVersion ?? ex.runNumber ?? 0}
          .currentRunNumber=${pfmLineage.currentRunNumber}
          .currentPfmRevision=${pfmLineage.currentPfmRevision}
          .previousPfmRunNumber=${pfmLineage.previousPfmRunNumber}
          .previousPfmRevision=${pfmLineage.previousPfmRevision}
          .pfmHeaderLabel=${pfmHeaderLabel}
          .pfmRevision=${ex.pfmRevision ?? 0}
          .pfmFinalized=${ex.pfmFinalized === true}
          .sessionKey=${resolveRunSessionKey(ex)}
          .parentPath=${this._activeContextPath()}
          .rootLabel=${this._currentMindmapRootLabel(ex)}
          .leafDescription=${this._currentContextLeafDescription(ex)}
          .visitedNodeKeys=${this._visitedNodeKeysForCurrentContext(ex)}
          .activePathNodeKeys=${this._activePathNodeKeysForCurrentContext(ex)}
          @pfm-node-selected=${(event: CustomEvent<{ nodeKey?: string; title?: string; source?: string }>) =>
            void this._handleMindmapNodeSelected(ex, event)}
        ></ead-pfm-mindmap-mermaid>
      </div>
    `;
  }

  private _renderPersistedPfmNodeSummary(ex: ProjectExecute): ReturnType<typeof html> {
    const results = ex.results || [];
    if (!results.length) return html``;
    return html`
      <section class="section-block">
        <div class="section-title">Persisted PFM Nodes (${results.length})</div>
        <div style="display:grid;gap:6px;">
          ${results.slice(0, 24).map((node, idx) => html`
            <div style="font-size:12px;line-height:1.4;color:var(--text);padding:7px 9px;border:1px solid color-mix(in srgb,var(--border) 70%,transparent);border-radius:8px;background:color-mix(in srgb,var(--card,#161920) 90%,transparent);">
              <strong>${idx + 1}.</strong>
              ${String(node.title || node.nodeKey || node.nodeId || "PFM node").replace(/\s+/g, " ")}
            </div>
          `)}
        </div>
        ${results.length > 24
          ? html`<div style="margin-top:8px;font-size:11px;color:var(--muted);">
              Showing first 24 persisted nodes.
            </div>`
          : ""}
      </section>
    `;
  }

  private _renderActivityFullscreen(): ReturnType<typeof html> {
    const image = this._fullscreenActivityImage;
    if (!image) return html``;
    return html`
      <div
        class="activity-fullscreen-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label=${this._t("runs.activity.fullscreenAria")}
        @click=${() => {
          this._fullscreenActivityImage = null;
        }}
      >
        <div class="activity-fullscreen-toolbar" @click=${(event: MouseEvent) => event.stopPropagation()}>
          <div class="activity-fullscreen-meta">
            <span class="activity-fullscreen-time">${this._formatClockTime(image.timestamp)}</span>
            <span>${image.activityText}</span>
          </div>
          <button
            class="activity-fullscreen-close"
            type="button"
            title=${this._t("runs.activity.closeFullscreen")}
            aria-label=${this._t("runs.activity.closeFullscreen")}
            @click=${() => {
              this._fullscreenActivityImage = null;
            }}
          >
            ${icons.x}
          </button>
        </div>
        <div class="activity-fullscreen-stage" @click=${(event: MouseEvent) => event.stopPropagation()}>
          <img src=${image.url} alt=${image.caption} />
        </div>
        <div class="activity-fullscreen-hint">${this._t("runs.activity.escapeHint")}</div>
      </div>
    `;
  }

  private _maximizeIcon(): ReturnType<typeof html> {
    return html`
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 3H3v5" />
        <path d="M16 3h5v5" />
        <path d="M21 16v5h-5" />
        <path d="M3 16v5h5" />
      </svg>
    `;
  }

  private _nodeReportExpandCollapseIcon(collapsed: boolean): ReturnType<typeof html> {
    return collapsed
      ? html`
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        `
      : html`
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 15l6-6 6 6" />
          </svg>
        `;
  }

  private _renderMindmapBreadcrumb(ex: ProjectExecute): ReturnType<typeof html> {
    const activeDepth = Math.max(0, Math.min(this._activeContextDepth, this._contextPath.length));
    return html`
      <div class="mindmap-header">
        <div class="mindmap-header__crumbs">
          <button
            class="crumb ${activeDepth === 0
              ? "crumb--active"
              : this._contextPath.length
                ? "crumb--visited-dim"
                : ""}"
            type="button"
            @click=${() => this._navigateToPathDepth(0)}
          >
            Top
          </button>
          ${this._contextPath.map((item, idx) => html`
            <span style="color:var(--muted);">›</span>
            <button
              class="crumb ${idx + 1 === activeDepth
                ? "crumb--active"
                : idx + 1 > activeDepth
                  ? "crumb--visited-dim"
                  : ""}"
              type="button"
              @click=${() => this._navigateToPathDepth(idx + 1)}
            >
              ${this._breadcrumbLabel(item.title)}
            </button>
          `)}
        </div>
      </div>
    `;
  }

  private _renderSelectedNodeReport(ex: ProjectExecute): ReturnType<typeof html> {
    if (this._shouldDeferPfmMindmap(ex)) {
      return html``;
    }
    const activePath = this._activeContextPath();
    const fallbackNode = activePath.length
      ? {
          nodeKey: activePath[activePath.length - 1]?.nodeKey || "",
          title: activePath[activePath.length - 1]?.title || "PFM Node",
          parents: activePath.slice(0, -1),
        }
      : null;
    const node = this._selectedNode ?? fallbackNode;
    if (!node?.nodeKey) {
      return html`
        <section class="section-block">
          <div class="section-title-row">
            <div class="section-title">Selected PFM Node Report</div>
            <button
              class="section-toggle-btn"
              type="button"
              title=${this._selectedNodeReportCollapsed ? "Expand node report panel" : "Collapse node report panel"}
              aria-label=${this._selectedNodeReportCollapsed ? "Expand node report panel" : "Collapse node report panel"}
              @click=${() => {
                this._selectedNodeReportCollapsed = !this._selectedNodeReportCollapsed;
              }}
            >
              ${this._nodeReportExpandCollapseIcon(this._selectedNodeReportCollapsed)}
            </button>
          </div>
          ${this._selectedNodeReportCollapsed
            ? ""
            : html`
                <div
                  style="padding:18px;text-align:center;border:1px dashed color-mix(in srgb,var(--border) 60%,transparent);border-radius:var(--radius-md);color:var(--muted);font-size:13px;line-height:1.6;"
                >
                  Click any node in the mindmap above to view its EAD node report (features, test cases, evidence).
                </div>
              `}
        </section>
      `;
    }
    const childCacheKey = buildMindmapCacheKey(resolveRunSessionKey(ex), [...node.parents, { nodeKey: node.nodeKey, title: node.title }]);
    const childCount = loadPfmMindmap(childCacheKey)?.nodes.length ?? 0;
    const mindmapLineage = this._mindmapLineageForRun(ex);
    const artifactReadId = this._pfmArtifactReadExecutionId(ex);
    const inheritsMap = Boolean(artifactReadId && artifactReadId !== ex.id);
    const reportPfmGenerationVersion = inheritsMap
      ? Math.max(
          0,
          mindmapLineage.lineage.previousPfmRunNumber,
          Number(ex.pfmSourceRunNumber || 0),
          Number(ex.pfmBaselineTreeVersion || 0),
        )
      : (ex.pfmGenerationVersion ?? 0);
    const reportPfmRevision = inheritsMap
      ? Math.max(0, mindmapLineage.lineage.previousPfmRevision, Number(ex.pfmRevision ?? 0))
      : (ex.pfmRevision ?? 0);
    const reportSnapshotPhase = inheritsMap
      ? "baseline_preview"
      : (ex.pfmSnapshotPhase || "");
    // pfm-node-report-ready: requestUpdate() only (child-count label). Never reload mindmap here.
    return html`
      <section class="section-block">
        <div class="section-title-row">
          <div class="section-title">Selected PFM Node Report</div>
          <button
            class="section-toggle-btn"
            type="button"
            title=${this._selectedNodeReportCollapsed ? "Expand node report panel" : "Collapse node report panel"}
            aria-label=${this._selectedNodeReportCollapsed ? "Expand node report panel" : "Collapse node report panel"}
            @click=${() => {
              this._selectedNodeReportCollapsed = !this._selectedNodeReportCollapsed;
            }}
          >
            ${this._nodeReportExpandCollapseIcon(this._selectedNodeReportCollapsed)}
          </button>
        </div>
        ${this._selectedNodeReportCollapsed
          ? ""
          : html`
              <ead-pfm-node-report-panel
                .executionId=${ex.id}
                .pfmArtifactReadExecutionId=${artifactReadId}
                .templateId=${ex.linkedTemplateId || ""}
                .runName=${ex.name || ""}
                .targetUrl=${ex.targetUrl || ""}
                .locale=${this.locale}
                .aiAnswerLanguage=${this._answerLanguageValue(ex)}
                .runStatus=${ex.status || ""}
                .pfmSnapshotPhase=${reportSnapshotPhase}
                .pfmGenerationVersion=${reportPfmGenerationVersion}
                .pfmRevision=${reportPfmRevision}
                .pfmFinalized=${ex.pfmFinalized === true}
                .sessionKey=${resolveRunSessionKey(ex)}
                .nodeKey=${node.nodeKey}
                .title=${node.title}
                .parents=${node.parents}
                .autoGenerate=${false}
                .autoGenerateOnDemand=${false}
                @pfm-node-report-ready=${() => this.requestUpdate()}
              ></ead-pfm-node-report-panel>
              ${childCount > 0
                ? html`<div style="margin-top:8px;font-size:11px;color:var(--muted);">Next Level PFM Nodes: ${childCount}</div>`
                : ""}
            `}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-runs-view": EadRunsView;
  }
}
